import { buildApprovedOfferDocument } from '@/domain/approvedOfferDocument';
﻿import React, { memo, useCallback, useRef, useState } from 'react';
import { Camera, FileText, FolderOpen, Loader2, Mic, Pencil, Save, Upload, X } from 'lucide-react';
import { useEffect } from 'react';
import { analyzeRoomVision, type VisionSuggestion } from '@/lib/aiService';
import { fileToBase64 } from '@/domain/vision';
import { calculateQuickEstimate } from '@/domain/quickEstimateEngine';
import type { EstimateRateType } from '@/domain/estimate.rates';
import {
  formatScopeRateBreakdown,
  getPrimaryRateLabel,
  getScopeRateSuggestion,
} from '@/domain/quickScopeRates';
import {
  formatWorkingRateValue,
  getWorkingRateItemsForQuickScope,
  WORKING_RATE_CATEGORY_LABELS,
  WORKING_RATE_STATUS_LABELS,
} from '@/domain/rates/workingRateBook';
import { type MaterialMode as DomainMaterialMode, type MaterialCostItem } from '@/domain/materialMode';
import { parseManualWorkScope } from '@/domain/scopeParser';
import { classifyVisionFailure, getVisionFailureMessage } from '@/domain/visionFailure';
import type { EngineInput, EngineResult, TradeEngine } from '@/domain/estimate.types';
import { runMarginShieldAudit, type MarginShieldAlert, type MarginShieldAuditResult } from '@/domain/marginShieldAudit';
import {
  buildApprovedOfferViewModel,
  setMarginShieldDecision,
  resolveMarginShieldDisplayStatus,
  type ApprovedOfferViewModel,
  type MarginShieldDecisionStatus,
} from '@/domain/approvedOffer';
import {
  getTestVariantNotice,
  resolveOfferDocumentVariant,
} from '@/domain/offerDocumentVariant';
import { getVatDocumentLabel, getVatSettlementLabel } from '@/domain/vatLabels';
import { createClientOfferPdfArtifact } from '@/lib/clientOfferPdf';
import {
  deliverPdfArtifact,
  FACEBOOK_INSTAGRAM_PDF_MESSAGE,
  PDF_DELIVERY_FAILED_MESSAGE,
  PDF_SHARE_CANCELLED_MESSAGE,
} from '@/lib/pdfDelivery';
import { safeFormatPLN } from '@/infra/utils';
import { useProjectStore, type Branza } from '@/store/useProjectStore';
import { CoreStatusBadge, CoreStatusBadgeRow } from '@/ui/components/CoreStatusBadge';
import { ScopeIntakeCard } from '@/ui/components/ScopeIntakeCard';
import {
  formatVoiceCommandPreview,
  parseVoiceCommand,
  type ParsedVoiceCommand,
} from '@/domain/parseVoiceCommand';
import {
  type ApartmentLayoutMode,
  type SurfaceCalculationResult,
} from '@/domain/surface/openingsCalculator';
import {
  buildQuickEngineGeometry,
  buildQuickOfferSurfaceSummary,
  calculateSurfaceForQuickForm,
  formatQuickDeductionM2,
  formatSurfaceM2,
  formatSurfaceValue,
  getQuickGeometrySource,
  parseNonNegativeInteger,
  parseNonNegativeNumber,
  parsePositiveNumber,
  resolveQuickAreaInputs,
  resolveQuickEstimatedAreaM2,
  resolveQuickWorkingAreaM2,
} from '@/domain/quickEstimateGeometry';
import {
  getQuickLengthFieldLabel,
  QUICK_APARTMENT_CEILING_LABEL,
  QUICK_APARTMENT_ESTIMATE_NOTE,
  QUICK_BATHROOM_CEILING_ONLY_HINT,
  QUICK_BATHROOM_CEILING_ONLY_LABEL,
  QUICK_READY_AREA_FIELD_LABEL,
  QUICK_ROOM_CEILING_LABEL,
  QUICK_SURFACE_MODE_OPTIONS,
  QUICK_SURFACE_MODE_QUESTION,
  shouldShowBathroomCeilingOnly,
} from '@/domain/quickSurfaceModeOptions';
import { OFFLINE_NETWORK_FEATURE_MESSAGE } from '@/lib/offlineCapability';
import { MATERIAL_CATALOG, type MaterialCatalogItem, type MaterialCatalogStandard } from '@/domain/materialCatalog';
import {
  consumeAIQuota,
  getAIQuotaStatus,
  isAIQuotaUnlimited,
  resolveAIPlanFromRole,
  type AIQuotaFailureKind,
} from '@/domain/aiQuota';
import { useAppContext } from '@/store/AppContext';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { usePwaInstall, type UsePwaInstallResult } from '@/hooks/usePwaInstall';
import { PwaInstallCard } from '@/ui/components/PwaInstallCard';
import { ManualItemsEditor } from '@/ui/components/ManualItemsEditor';
import {
  createManualEstimateItem,
  removeManualEstimateItem,
  updateManualEstimateItem,
  type ManualEstimateItem,
  type ManualItemInput,
} from '@/domain/manualEstimateItems';
import type { Project } from '@/store/AppContext';
import {
  createQuickEstimateSnapshot,
  readQuickEstimateSnapshot,
  isUsableQuickProjectSnapshot,
  type QuickEstimateSnapshot,
} from '@/domain/quickEstimateSnapshot';
import {
  buildMarginShieldDecisionScopeKey,
  getBrowserQuickDraftStorage,
  loadQuickActiveDraft,
  reconcileMarginShieldDecisions,
  resolveQuickDraftOwner,
  saveQuickActiveDraft,
  type LoadedQuickActiveDraft,
} from '@/domain/quickActiveDraft';
import { buildQuickProjectUpdate } from '@/domain/quickProjectUpdate';
import { persistQuickProject } from '@/domain/quickProjectPersistence';
import { canStartQuickProjectSave } from '@/domain/quickSavePrerequisites';
import {
  calculatePercentageMoney,
  roundMoney,
  sumMoney,
} from '@/domain/money';
import { AI_TIMEOUTS_MS } from '@/config/aiRuntimeConfig';
import { trackProductEvent } from '@/lib/productAnalytics';
import { logOperationalError } from '@/lib/operationalErrorLog';

interface QuickPhotoEstimateProps {
  onClose?: () => void;
  onAdvanced: () => void;
  onProjects?: () => void;
  publicMode?: boolean;
  initialProject?: Project | null;
  onProjectSaved?: (id: string) => void;
  /** Explicit "Nowa wycena": the parent clears the local draft and remounts the screen. */
  onNewEstimate?: () => void;
}

type UploadedPhoto = {
  id: string;
  file: File;
  previewUrl: string;
};

type QuickFormState = {
  surfaceMode: QuickSurfaceMode;
  readyWorkingArea: string;
  manualHeight: string;
  roomLength: string;
  roomWidth: string;
  doorCount: string;
  largeOpeningsArea: string;
  includeCeiling: boolean;
  apartmentArea: string;
  apartmentHeight: string;
  apartmentDoorCount: string;
  apartmentLargeOpeningsArea: string;
  bathroomArea: string;
  bathroomCeilingOnly: boolean;
  apartmentLayout: ApartmentLayoutMode;
  pavingMode?: 'surface' | 'edges_only';
  pavingArea: string;
  pavingLength: string;
  pavingWidth: string;
  curbLength: string;
  pavingThickness: QuickPavingThickness;
  pavingSubbase: QuickPavingSubbase;
  pavingTransport: QuickPavingTransport;
  manualScope: QuickScope;
  customScope: string;
  laborRateMode: LaborRateMode;
  customLaborRate: string;
  materialMode: QuickMaterialMode;
  materialDiscountPercent: string;
  materialClientPrice: string;
  vatMode: QuickVatMode;
  contractorName: string;
  contractorNip: string;
  contractorContact: string;
};

type QuickSurfaceMode = 'ready' | 'room' | 'apartment' | 'wall';
type QuickScope = 'painting' | 'plaster_painting' | 'scrape_prime_plaster_paint' | 'panels' | 'bathroom' | 'paving' | 'other';
type LaborRateMode = 'suggested' | 'custom';
type QuickMaterialMode = 'client' | 'contractor' | 'info';
type QuickVatMode = 'no_vat' | 'vat_8' | 'vat_23' | 'confirm';
type QuickPavingThickness = '6cm' | '8cm' | 'confirm';
type QuickPavingSubbase = 'ready' | 'todo' | 'confirm';
type QuickPavingTransport = 'client' | 'contractor' | 'confirm';
type ManualAreaSource = 'manual' | 'voice';
type VisionStatusMessage = {
  tone: 'success' | 'warning';
  text: string;
} | null;

type QuickEstimateSummary = {
  scope: QuickScope;
  scopeLabel: string;
  scopeSource: SourceBadgeTone;
  areaM2: number;
  floorAreaM2?: number;
  workingAreaM2?: number;
  floorAreaSource: AreaSource;
  manualHeightM?: number;
  surfaceCalculation?: SurfaceCalculationResult;
  inputSourceLabel: string;
  priceFrom: number;
  priceTo: number;
  confidence?: number;
  hasRealConfidence: boolean;
  isLocalMode: boolean;
  /** True only when a real Vision analysis produced a result for this estimate. */
  hasVisionAnalysis: boolean;
  visionSummary: VisionDetectionSummary;
  aiObservations: string[];
  risks: string[];
  engineInput: EngineInput;
  estimate: EngineResult;
  materialMode: QuickMaterialMode;
  materialDiscountPercent: number;
  materialClientPrice?: number;
  vatMode: QuickVatMode;
  contractorName?: string;
  contractorNip?: string;
  contractorContact?: string;
  paving?: {
    workingAreaM2: number;
    curbLengthM?: number;
    /** Legacy saved offers keep their approved flat charge until explicitly recalculated. */
    useMeasuredEdges?: boolean;
    thicknessLabel?: string;
    subbaseLabel?: string;
    transportLabel: string;
  };
};

type AreaSource = 'manual' | 'voice' | 'vision' | 'fallback';
type SourceBadgeTone = 'manual' | 'voice' | 'vision' | 'local' | 'confirm';

type VisionDetectionSummary = {
  roomType: string;
  workType: string;
  walls: string;
  ceiling: string;
  doors: string;
  windows: string;
  /** Undefined when no analysed photo reported a substrate condition. */
  surfaceCondition?: string;
  surfaceConditionSource?: SourceBadgeTone;
  chips: string[];
  manualChecks: string[];
  canRenderOverlay: boolean;
};

const MAX_PHOTOS = 5;
const MIN_CONFIDENCE_TO_SHOW = 10;
const VISION_ERROR_PREFIX = 'VISION_QUOTA:';
const QUICK_SCOPE_LABELS: Record<QuickScope, string> = {
  painting: 'malowanie',
  plaster_painting: 'gładź + malowanie',
  scrape_prime_plaster_paint: 'skrobanie + grunt + gładź + malowanie',
  panels: 'panele',
  bathroom: 'łazienka',
  paving: 'bruk',
  other: 'Inne / własny zakres',
};

const scopeOptions = Object.entries(QUICK_SCOPE_LABELS) as Array<[QuickScope, string]>;
const FIELD_CONTROL_CLASS =
  'min-h-12 w-full rounded-lg border border-[#2a2f38] bg-[#0f1217] px-3 text-base text-white outline-none placeholder:text-slate-500 focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20';
const BATHROOM_SCOPE_NOTICE =
  'To wygląda jak wycena samego układania płytek. Pełny remont łazienki wymaga osobnego sprawdzenia: hydroizolacja, przygotowanie, odpady, biały montaż, silikon i narożniki.';
const VISION_MANUAL_DIMENSIONS_MESSAGE =
  'Nie udało się wiarygodnie odczytać wymiarów ze zdjęcia. Zdjęcia zostały zapisane. Podaj metraż ręcznie, a ProWycena policzy dalej lokalnie.';
const DEFAULT_FORM: QuickFormState = {
  surfaceMode: 'room',
  readyWorkingArea: '',
  manualHeight: '',
  roomLength: '',
  roomWidth: '',
  doorCount: '',
  largeOpeningsArea: '',
  includeCeiling: true,
  apartmentArea: '',
  apartmentHeight: '2.65',
  apartmentDoorCount: '',
  apartmentLargeOpeningsArea: '',
  bathroomArea: '',
  bathroomCeilingOnly: false,
  apartmentLayout: 'standard',
  pavingArea: '',
  pavingLength: '',
  pavingWidth: '',
  curbLength: '',
  pavingThickness: 'confirm',
  pavingSubbase: 'confirm',
  pavingTransport: 'confirm',
  manualScope: 'plaster_painting',
  customScope: '',
  laborRateMode: 'suggested',
  customLaborRate: '',
  materialMode: 'client',
  materialDiscountPercent: '',
  materialClientPrice: '',
  vatMode: 'no_vat',
  contractorName: '',
  contractorNip: '',
  contractorContact: '',
};

type QuickEstimateProjectSnapshot = QuickEstimateSnapshot<
  QuickFormState,
  QuickEstimateSummary,
  ManualEstimateItem,
  MarginShieldDecisionStatus
>;

// Form fields kept in the active local draft (see `@/domain/quickActiveDraft`).
// Contractor identity/contact is never stored; it comes back from the profile.
const QUICK_ESTIMATE_DRAFT_FIELDS = [
  'surfaceMode',
  'readyWorkingArea',
  'manualHeight',
  'roomLength',
  'roomWidth',
  'doorCount',
  'largeOpeningsArea',
  'includeCeiling',
  'apartmentArea',
  'apartmentHeight',
  'apartmentDoorCount',
  'apartmentLargeOpeningsArea',
  'bathroomArea',
  'bathroomCeilingOnly',
  'apartmentLayout',
  'pavingMode',
  'pavingArea',
  'pavingLength',
  'pavingWidth',
  'curbLength',
  'pavingThickness',
  'pavingSubbase',
  'pavingTransport',
  'manualScope',
  'customScope',
  'laborRateMode',
  'customLaborRate',
  'materialMode',
  'materialDiscountPercent',
  'materialClientPrice',
  'vatMode',
] as const satisfies ReadonlyArray<keyof QuickFormState>;

type QuickEstimateDraftField = (typeof QUICK_ESTIMATE_DRAFT_FIELDS)[number];
type QuickEstimateDraftForm = Partial<Pick<QuickFormState, QuickEstimateDraftField>>;

const PAVING_DEFAULT_RATE_M2 = 95;
const PAVING_THICKNESS_LABELS: Record<QuickPavingThickness, string> = {
  '6cm': '6 cm',
  '8cm': '8 cm',
  confirm: 'do ustalenia',
};
const PAVING_SUBBASE_LABELS: Record<QuickPavingSubbase, string> = {
  ready: 'gotowa',
  todo: 'do wykonania',
  confirm: 'do ustalenia',
};
const PAVING_TRANSPORT_LABELS: Record<QuickPavingTransport, string> = {
  client: 'klient',
  contractor: 'wykonawca',
  confirm: 'do ustalenia',
};

function isPavingScope(scope: QuickScope): boolean {
  return scope === 'paving';
}

/**
 * Working area for paving: an explicitly entered surface always wins;
 * otherwise fall back to length × width. No wall/ceiling geometry applies.
 */
function computePavingWorkingAreaM2(area?: number, length?: number, width?: number): number {
  if (area && area > 0) return round1(area);
  if (length && length > 0 && width && width > 0) return round1(length * width);
  return 0;
}

function pickQuickEstimateDraftForm(form: QuickFormState): QuickEstimateDraftForm {
  return QUICK_ESTIMATE_DRAFT_FIELDS.reduce<QuickEstimateDraftForm>((draft, field) => {
    draft[field] = form[field] as never;
    return draft;
  }, {});
}

function sanitizeQuickEstimateDraftForm(value: unknown): QuickEstimateDraftForm {
  if (!value || typeof value !== 'object') return {};

  const parsed = value as Partial<Record<QuickEstimateDraftField, unknown>>;
  return QUICK_ESTIMATE_DRAFT_FIELDS.reduce<QuickEstimateDraftForm>((draft, field) => {
    const defaultValue = DEFAULT_FORM[field];
    const candidate = parsed[field];

    if (typeof defaultValue === 'boolean') {
      if (typeof candidate === 'boolean') draft[field] = candidate as never;
      return draft;
    }

    if (typeof candidate === 'string') {
      draft[field] = candidate as never;
    }

    return draft;
  }, {});
}

function buildFormFromDraft(draftForm: QuickEstimateDraftForm): QuickFormState {
  return {
    ...DEFAULT_FORM,
    ...draftForm,
    contractorName: '',
    contractorNip: '',
    contractorContact: '',
  };
}

function getDecisionScopeKey(form: QuickFormState): string {
  return buildMarginShieldDecisionScopeKey(form);
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function translateVisionText(value: string): string {
  const normalized = value.toLowerCase();

  if (normalized.trim() === 'painted') {
    return 'Ściany pomalowane / dobry stan.';
  }

  if (normalized.includes('walls appear to be painted') || normalized.includes('painted and in good condition')) {
    return 'Ściany wyglądają na pomalowane i w dobrym stanie.';
  }

  if (normalized.includes('one door is visible') || normalized.includes('door is visible')) {
    return 'Widoczne są jedne drzwi / futryna.';
  }

  if (normalized.includes('no windows are directly visible') || normalized.includes('no windows')) {
    return 'Nie widać okien w pomieszczeniu ze zdjęcia.';
  }

  if (normalized.includes('no construction materials') || normalized.includes('paint buckets')) {
    return 'Brak widocznych materiałów budowlanych w kadrze.';
  }

  if (normalized.includes('only one wall is clearly visible')) {
    return 'Na zdjęciu widoczna jest tylko jedna ściana. Dokładny metraż wymaga dodatkowych zdjęć lub ręcznego potwierdzenia.';
  }

  if (normalized.includes('visible wall')) {
    return 'Widoczna ściana wymaga ręcznego potwierdzenia metrażu.';
  }

  if (normalized.includes('reference wall')) {
    return 'Widoczna ściana wymaga ręcznego potwierdzenia metrażu.';
  }

  if (/[a-z]/i.test(value) && !/[ąćęłńóśźż]/i.test(value)) {
    return 'Obserwacja ze zdjęcia wymaga potwierdzenia przez wykonawcę.';
  }

  return value;
}

function toDomainMaterialMode(mode: QuickMaterialMode): DomainMaterialMode {
  if (mode === 'contractor') return 'CONTRACTOR_MATERIALS';
  if (mode === 'info') return 'INFO_MATERIALS';
  return 'CLIENT_MATERIALS';
}

function fromDomainMaterialMode(mode: DomainMaterialMode): QuickMaterialMode {
  if (mode === 'CONTRACTOR_MATERIALS') return 'contractor';
  if (mode === 'INFO_MATERIALS') return 'info';
  return 'client';
}

function getQuickMaterialModeLabel(mode: QuickMaterialMode): string {
  if (mode === 'contractor') return 'Materiał po stronie wykonawcy';
  if (mode === 'info') return 'Materiał informacyjnie';
  return 'Materiał po stronie klienta';
}

function getSettlementModeForVat(mode: QuickVatMode): 'no_vat' | 'vat_8' | 'vat_23' {
  if (mode === 'vat_8') return 'vat_8';
  if (mode === 'vat_23') return 'vat_23';
  return 'no_vat';
}

function calculateRetailMaterialTotal(materials: MaterialCostItem[] | undefined): number {
  return sumMoney((materials ?? []).map((item) => item.estimatedTotal ?? 0));
}

function calculateMaterialInternalPreview(materials: MaterialCostItem[] | undefined, discountPercent: number, manualClientPrice?: number) {
  const materialRetailPrice = calculateRetailMaterialTotal(materials);
  const clientPrice = manualClientPrice && manualClientPrice > 0 ? manualClientPrice : materialRetailPrice;
  const safeDiscount = clamp(discountPercent, 0, 80);
  const internalMaterialCost = calculatePercentageMoney(clientPrice, 1 - safeDiscount / 100);
  const internalMaterialGain = Math.max(0, clientPrice - internalMaterialCost);

  return {
    materialRetailPrice: clientPrice,
    discountPercent: safeDiscount,
    internalMaterialCost,
    internalMaterialGain,
    showInternalMaterialDataToClient: false,
  };
}

function getPdfContactPatch(value: string): { phone?: string; email?: string } {
  const contact = value.trim();
  if (!contact) return {};
  if (contact.includes('@')) return { email: contact };
  return { phone: contact };
}

function normalizePhotoMimeType(value: string): 'image/jpeg' | 'image/png' | 'image/webp' {
  if (value === 'image/png' || value === 'image/webp') return value;
  return 'image/jpeg';
}

function hasInputValue(value: string | undefined): boolean {
  return Boolean(value?.trim());
}

function formatCompactPLN(value: number): string {
  const safeValue = Number.isFinite(value) ? value : 0;
  return `${Math.round(safeValue).toLocaleString('pl-PL')} zł`;
}

function formatPriceRange(from: number, to: number): string {
  return `${formatCompactPLN(from)} – ${formatCompactPLN(to)}`;
}

function formatKnownArea(value: number | undefined, source: AreaSource): string {
  if (!value || value <= 0) {
    return 'do potwierdzenia przez wykonawcę';
  }

  if (source === 'manual') return `${formatSurfaceM2(value)} - przyjęta z ręcznej korekty`;
  if (source === 'voice') return `${formatSurfaceM2(value)} - przyjęta z głosu`;
  return `${formatSurfaceM2(value)} - do potwierdzenia`;
}

function getInputSourceLabel(data: { areaSource: AreaSource; hasVisionAnalysis: boolean }): string {
  if (data.areaSource === 'voice' && data.hasVisionAnalysis) return 'głos + zdjęcie jako kontekst';
  if (data.areaSource === 'voice') return 'głos';
  if (data.areaSource === 'manual' && data.hasVisionAnalysis) return 'wpis ręczny + zdjęcie jako kontekst';
  if (data.areaSource === 'manual') return 'wpis ręczny';
  return data.hasVisionAnalysis ? 'zdjęcie jako kontekst + ręczne dane' : 'lokalne założenie do potwierdzenia';
}

function validateQuickEstimateForm(data: {
  surfaceMode?: QuickSurfaceMode;
  readyWorkingAreaRaw?: string;
  readyWorkingAreaM2?: number;
  manualScope: QuickScope;
  customScope?: string;
  manualHeightRaw?: string;
  manualHeightM?: number;
  roomLengthRaw?: string;
  roomLengthM?: number;
  roomWidthRaw?: string;
  roomWidthM?: number;
  doorCountRaw?: string;
  doorCount?: number;
  largeOpeningsAreaRaw?: string;
  largeOpeningsAreaM2?: number;
  apartmentAreaRaw?: string;
  apartmentAreaM2?: number;
  apartmentHeightRaw?: string;
  apartmentHeightM?: number;
  apartmentDoorCountRaw?: string;
  apartmentDoorCount?: number;
  apartmentLargeOpeningsAreaRaw?: string;
  apartmentLargeOpeningsAreaM2?: number;
  bathroomAreaRaw?: string;
  bathroomAreaM2?: number;
  customLaborRateRaw?: string;
  customLaborRateM2?: number;
  laborRateMode?: QuickFormState['laborRateMode'];
  pavingAreaM2?: number;
  pavingLengthM?: number;
  pavingWidthM?: number;
  curbLengthM?: number;
  pavingMode?: 'surface' | 'edges_only';
}): string | null {
  if (!data.manualScope) {
    return 'Wybierz zakres prac albo opisz własny zakres.';
  }

  if (data.manualScope === 'other' && !data.customScope?.trim()) {
    return 'Wybierz zakres prac albo opisz własny zakres.';
  }

  if (data.manualScope === 'paving') {
    const workingArea = computePavingWorkingAreaM2(data.pavingAreaM2, data.pavingLengthM, data.pavingWidthM);
    if (workingArea <= 0) {
      if (data.pavingMode === 'surface') return 'Podaj powierzchnię do ułożenia albo długość i szerokość terenu.';
      if (data.pavingMode === 'edges_only' && !data.curbLengthM) return 'Podaj długość obrzeży / krawężników większą od 0 mb.';
      if (!data.curbLengthM) return 'Podaj powierzchnię do ułożenia albo długość obrzeży.';
      if (data.laborRateMode !== 'custom' || !data.customLaborRateM2) {
        return 'Dla samych obrzeży podaj własną stawkę zł/mb.';
      }
    }
    if (data.laborRateMode === 'custom' && (!data.customLaborRateM2 || data.customLaborRateM2 <= 0)) {
      return 'Stawka własna musi być liczbą większą od 0 albo przełącz na stawkę sugerowaną.';
    }
    return null;
  }

  if (data.surfaceMode === 'ready') {
    if (!data.readyWorkingAreaM2) {
      return 'Podaj gotową powierzchnię roboczą większą od 0.';
    }
  } else if (data.surfaceMode === 'apartment') {
    if (!data.apartmentAreaM2) return 'Podaj powierzchnię mieszkania większą od 0.';
    if (hasInputValue(data.apartmentHeightRaw) && !data.apartmentHeightM) {
      return 'Wysokość musi być liczbą większą od 0, np. 2.65.';
    }
    if (hasInputValue(data.apartmentDoorCountRaw) && data.apartmentDoorCount === undefined) {
      return 'Liczba drzwi musi być liczbą całkowitą 0 lub większą.';
    }
    if (hasInputValue(data.apartmentLargeOpeningsAreaRaw) && data.apartmentLargeOpeningsAreaM2 === undefined) {
      return 'Nietypowe otwory do odliczenia muszą być liczbą 0 lub większą albo zostaw pole puste.';
    }
    if (hasInputValue(data.bathroomAreaRaw) && data.bathroomAreaM2 === undefined) {
      return 'Powierzchnia łazienki musi być liczbą 0 lub większą albo zostaw pole puste.';
    }
  } else {
    if (hasInputValue(data.manualHeightRaw) && !data.manualHeightM) {
      return 'Wysokość musi być liczbą większą od 0, np. 2.65.';
    }

    if (!data.roomLengthM) {
      return data.surfaceMode === 'wall'
        ? 'Podaj długość ściany większą od 0.'
        : 'Podaj długość pomieszczenia większą od 0.';
    }
    if (data.surfaceMode === 'room' && !data.roomWidthM) {
      return 'Podaj szerokość pomieszczenia większą od 0.';
    }
    if (!data.manualHeightM) return 'Podaj wysokość większą od 0.';

    if (hasInputValue(data.doorCountRaw) && data.doorCount === undefined) {
      return 'Liczba drzwi musi być liczbą całkowitą 0 lub większą.';
    }

    if (hasInputValue(data.largeOpeningsAreaRaw) && data.largeOpeningsAreaM2 === undefined) {
      return 'Nietypowe otwory do odliczenia muszą być liczbą 0 lub większą albo zostaw pole puste.';
    }
  }

  if (data.laborRateMode === 'custom' && (!data.customLaborRateM2 || data.customLaborRateM2 <= 0)) {
    return 'Stawka własna musi być liczbą większą od 0 albo przełącz na stawkę sugerowaną.';
  }

  return null;
}

function shouldShowBathroomScopeNotice(result: QuickEstimateSummary): boolean {
  const text = [
    result.scope,
    result.scopeLabel,
    result.engineInput.trade,
    result.engineInput.tradeEngine,
    result.engineInput.scopeText,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return (
    text.includes('łazien') ||
    text.includes('lazien') ||
    text.includes('płyt') ||
    text.includes('plyt') ||
    text.includes('gres')
  );
}

function getVisionQuotaLabel(isAvailable: boolean, hasAppVisionBypass: boolean): string {
  if (hasAppVisionBypass || isAvailable) return 'Zdjęcie opcjonalne';
  return 'Tryb ręczny';
}

function getVisionQuotaMessage(kind: AIQuotaFailureKind): string {
  if (kind === 'app_plan_limit') {
    return 'Limit analizy zdjęć wykorzystany. Ręczna wycena działa dalej.';
  }

  if (kind === 'provider_limit') {
    return 'Limit analizy zdjęć po stronie dostawcy. Ręczna wycena działa dalej.';
  }

  return VISION_MANUAL_DIMENSIONS_MESSAGE;
}

/**
 * Komunikat o nieudanej analizie zdjecia. Awaria backendu, zla trasa API,
 * timeout i brak sieci maja wlasne teksty — zadne z nich nie moze brzmiec jak
 * "nie odczytano wymiarów ze zdjęcia", bo zdjecie nie jest wtedy przyczyna.
 */
function getVisionErrorMessage(error: unknown): string {
  const reason =
    error instanceof Error && error.message.startsWith(VISION_ERROR_PREFIX)
      ? error.message.slice(VISION_ERROR_PREFIX.length)
      : undefined;

  return getVisionFailureMessage(classifyVisionFailure(reason));
}

function getSourceBadgeLabel(source: AreaSource | SourceBadgeTone): string {
  if (source === 'voice') return 'Głos';
  if (source === 'manual') return 'Wpis ręczny';
  if (source === 'vision') return 'Zdjęcie: kontekst';
  if (source === 'local') return 'Wyliczono z wpisanych danych';
  return 'Do potwierdzenia';
}

function getSourceBadgeTone(source: AreaSource | SourceBadgeTone): SourceBadgeTone {
  if (source === 'voice') return 'voice';
  if (source === 'manual') return 'manual';
  if (source === 'vision') return 'vision';
  if (source === 'local') return 'local';
  return 'confirm';
}

/**
 * Returns a substrate-condition label ONLY when a real Vision result reported one.
 * `null` means "no analysed photo said anything about the substrate" — the caller
 * must then render nothing instead of a default-data placeholder.
 */
function getSurfaceConditionLabel(
  visions: VisionSuggestion[]
): { value: string; source: SourceBadgeTone } | null {
  const condition = visions[0]?.surfaceCondition?.trim();
  if (!condition) {
    return null;
  }

  const normalized = condition.toLowerCase();
  if (
    normalized.includes('good') ||
    normalized.includes('dobr') ||
    normalized.includes('fresh') ||
    normalized.includes('swiez') ||
    normalized.includes('śwież') ||
    normalized.includes('smooth') ||
    normalized.includes('gladka') ||
    normalized.includes('gładka')
  ) {
    return { value: 'wygląda na dobry', source: 'vision' };
  }

  return { value: translateVisionText(condition), source: 'vision' };
}

function getPrimaryRateType(scope: QuickScope): EstimateRateType {
  if (scope === 'bathroom') return 'bath_tiles';
  if (scope === 'paving') return 'paving_laying';
  if (scope === 'panels') return 'painting';
  if (scope === 'plaster_painting' || scope === 'scrape_prime_plaster_paint') return 'plaster';
  return 'painting';
}

function buildCustomUserRates(scope: QuickScope, region: string | undefined, customLaborRateM2?: number): EngineInput['userRates'] | undefined {
  if (!customLaborRateM2) return undefined;

  const suggestion = getScopeRateSuggestion(scope, region ?? '');
  const rateComponents = suggestion.components.filter((component): component is typeof component & { id: EstimateRateType } =>
    ['scraping', 'primer', 'painting', 'plaster', 'sanding', 'bath_tiles', 'paving_laying'].includes(component.id)
  );

  if (rateComponents.length <= 1) {
    return {
      [getPrimaryRateType(scope)]: customLaborRateM2,
    };
  }

  const currentTotal = rateComponents.reduce((sum, component) => sum + component.rateDefault, 0);
  if (currentTotal <= 0) return undefined;

  return Object.fromEntries(
    rateComponents.map((component) => [
      component.id,
      roundMoney((component.rateDefault / currentTotal) * customLaborRateM2),
    ])
  ) as EngineInput['userRates'];
}

function buildVisionSummary(data: {
  visions: VisionSuggestion[];
  scope: QuickScope;
  scopeLabel: string;
}): VisionDetectionSummary {
  const firstVision = data.visions[0];
  const surfaceCondition = getSurfaceConditionLabel(data.visions);
  // Chips describe what an analysed photo showed. Without a real Vision result
  // there is nothing photographic to chip, so the list stays empty.
  const chips = firstVision
    ? [
        data.scopeLabel,
        ...(data.scope !== 'paving' ? ['ściany', 'sufit'] : []),
        ...(firstVision.doorCount ? ['drzwi'] : []),
        ...(firstVision.standardWindowCount || firstVision.bigWindowsArea ? ['okna'] : []),
      ]
    : [];

  return {
    roomType: data.scope === 'bathroom' ? 'łazienka' : 'do potwierdzenia',
    workType: data.scopeLabel || 'do potwierdzenia',
    walls: data.scope !== 'paving' ? 'do potwierdzenia' : 'nie dotyczy',
    ceiling: data.scope !== 'paving' ? 'do potwierdzenia' : 'nie dotyczy',
    doors: firstVision?.doorCount ? `${firstVision.doorCount}` : 'do potwierdzenia',
    windows: firstVision?.standardWindowCount || firstVision?.bigWindowsArea ? 'wykryto / do przeliczenia' : 'do potwierdzenia',
    surfaceCondition: surfaceCondition?.value,
    surfaceConditionSource: surfaceCondition?.source,
    chips: [...new Set(chips)],
    manualChecks: [
      'łączny metraż ścian',
      'czy w zakres wchodzi sufit',
      'liczba okien / drzwi',
      'czy wymagane jest gruntowanie',
    ],
    canRenderOverlay: false,
  };
}

function getRoomTypeLabel(roomType?: VisionSuggestion['roomType']): string {
  if (roomType === 'pokoj') return 'pokój';
  if (roomType === 'lazienka') return 'łazienka';
  if (roomType === 'kuchnia') return 'kuchnia';
  if (roomType === 'korytarz') return 'korytarz';
  return 'do potwierdzenia';
}

function getMaterialCatalogForScope(scope: QuickScope): MaterialCatalogItem[] {
  const standardPaint = MATERIAL_CATALOG.find((item) => item.id === 'paint_standard_l');
  const standardPrimer = MATERIAL_CATALOG.find((item) => item.id === 'primer_standard_l');
  const standardPlaster = MATERIAL_CATALOG.find((item) => item.id === 'plaster_standard_kg');

  if (scope === 'plaster_painting') {
    return [standardPlaster, standardPaint].filter(Boolean) as MaterialCatalogItem[];
  }

  if (scope === 'scrape_prime_plaster_paint') {
    return [standardPrimer, standardPlaster, standardPaint].filter(Boolean) as MaterialCatalogItem[];
  }

  if (scope === 'painting' || scope === 'other') {
    return standardPaint ? [standardPaint] : [];
  }

  return [];
}

function formatMaterialStandard(standard: MaterialCatalogStandard): string {
  if (standard === 'ekonomiczny') return 'Ekonomiczny';
  if (standard === 'premium') return 'Premium';
  return 'Standard';
}

function buildAiObservations(visions: VisionSuggestion[]): string[] {
  const firstVision = visions[0];
  if (!firstVision) return [];

  const observations: string[] = [];
  const surface = (firstVision.surfaceCondition ?? '').toLowerCase();
  const notes = (firstVision.notes ?? []).join(' ').toLowerCase();
  const surfaceText = `${surface} ${notes}`;

  if (firstVision.surfaceCondition) {
    observations.push(translateVisionText(firstVision.surfaceCondition));
  }

  if (
    surfaceText.includes('good') ||
    surfaceText.includes('dobr') ||
    surfaceText.includes('fresh') ||
    surfaceText.includes('swiez') ||
    surfaceText.includes('śwież') ||
    surfaceText.includes('gladka') ||
    surfaceText.includes('gładka') ||
    surfaceText.includes('smooth')
  ) {
    observations.push('Stan podłoża wygląda na dobry.');
    observations.push('Nie widać oczywistych trudnych napraw.');
  }

  if ((firstVision.doorCount ?? 0) > 0) {
    observations.push(`Widoczne drzwi / futryny: ${firstVision.doorCount}.`);
  }

  if ((firstVision.standardWindowCount ?? 0) > 0 || (firstVision.bigWindowsArea ?? 0) > 0) {
    observations.push('Widoczne okna lub większe otwory do sprawdzenia w obmiarze.');
  }

  for (const note of firstVision.notes ?? []) {
    observations.push(translateVisionText(note));
  }

  return [...new Set(observations)].slice(0, 4);
}

function buildEngineInput(data: {
  visions: VisionSuggestion[];
  manualAreaM2?: number;
  manualWorkingAreaM2?: number;
  manualAreaSource?: ManualAreaSource | null;
  manualHeightM?: number;
  surfaceCalculation?: SurfaceCalculationResult;
  manualScope: QuickScope;
  customScope?: string;
  customLaborRateM2?: number;
  edgeLengthM?: number;
  region?: string;
}): { input: EngineInput; scope: QuickScope; scopeLabel: string; confidence?: number; hasRealConfidence: boolean; isLocalFallback: boolean; risks: string[]; areaSource: AreaSource } {
  const firstVision = data.visions[0];
  let manualResolution: ReturnType<typeof parseManualWorkScope> | undefined;
  let scopeParseFailed = false;
  if (data.manualScope === 'other') {
    try {
      manualResolution = parseManualWorkScope(data.customScope ?? '');
    } catch {
      scopeParseFailed = true;
      manualResolution = undefined;
    }
  }
  const selectedScope = manualResolution
    ? manualResolution.hasPanels
      ? 'panels'
      : 'other'
    : data.manualScope;
  const scope = selectedScope;
  const edgesOnly = scope === 'paving' && data.manualAreaM2 === 0 && (data.edgeLengthM ?? 0) > 0;
  const surfaceCalculation = data.surfaceCalculation;
  const areaSource: AreaSource = edgesOnly || data.manualAreaM2 || data.manualWorkingAreaM2 || surfaceCalculation ? data.manualAreaSource ?? 'manual' : 'fallback';
  const geometry = buildQuickEngineGeometry({
    surfaceCalculation,
    manualAreaM2: data.manualAreaM2,
    manualWorkingAreaM2: data.manualWorkingAreaM2,
    manualHeightM: data.manualHeightM,
    visionDoorCount: firstVision?.doorCount,
    visionBigWindowsArea: firstVision?.bigWindowsArea,
  });
  const areaM2 = geometry.floorArea;
  const tradeEngine: TradeEngine = scope === 'bathroom' ? 'bathroom' : scope === 'paving' ? 'paving' : 'painting';
  const scopeText = manualResolution?.scopeText || QUICK_SCOPE_LABELS[scope];
  const hasRealConfidence =
    typeof firstVision?.confidence === 'number' &&
    Number.isFinite(firstVision.confidence) &&
    firstVision.confidence >= MIN_CONFIDENCE_TO_SHOW;
  const confidence = hasRealConfidence ? clamp(Math.round(firstVision.confidence ?? 0), 0, 100) : undefined;
  const risks = [
    ...new Set([
      // Fragmenty zakresu, ktorych silnik NIE policzyl, ida pierwsze —
      // lista jest ucinana do 4 pozycji i ogolna podpowiedz nie moze ich wypchnac.
      ...(manualResolution?.confirmationReasons ?? []),
      ...(firstVision?.notes ?? []),
      ...(scope.includes('paint') || scope === 'painting' ? ['sprawdź gruntowanie i poprawki podłoża'] : []),
      ...(scope === 'bathroom' ? ['sprawdź hydroizolację i punkty wodne'] : []),
      ...(scope === 'paving' ? ['sprawdź podbudowę i odprowadzenie wody'] : []),
    ]),
  ].slice(0, 4);

  return {
    scope,
    scopeLabel: edgesOnly ? 'Obrzeża / krawężniki' : manualResolution?.scopeLabel ?? QUICK_SCOPE_LABELS[scope],
    confidence,
    hasRealConfidence,
    isLocalFallback: areaSource === 'fallback' || data.visions.length === 0,
    areaSource,
    risks,
    input: {
      tradeEngine,
      trade: tradeEngine === 'bathroom' ? 'lazienka' : tradeEngine === 'paving' ? 'brukarstwo' : 'malarz',
      projectMode: geometry.projectMode,
      length: geometry.length,
      width: geometry.width,
      height: geometry.height,
      floorArea: geometry.floorArea,
      manualWorkingAreaM2: geometry.manualWorkingAreaM2,
      doorCount: geometry.doorCount,
      bigWindowsArea: geometry.bigWindowsArea,
      includeCeiling: geometry.includeCeiling,
      hasPainting: manualResolution ? manualResolution.work.painting : scope !== 'bathroom' && scope !== 'paving',
      hasPrimer: manualResolution ? manualResolution.work.primer : scope === 'scrape_prime_plaster_paint',
      hasScraping: manualResolution ? manualResolution.work.scraping : scope === 'scrape_prime_plaster_paint',
      hasHydro: false,
      work: manualResolution?.work,
      scopeText,
      region: data.region,
      baseRate: scope === 'painting' ? data.customLaborRateM2 : undefined,
      userRates: edgesOnly ? { paving_edges: data.customLaborRateM2 } : buildCustomUserRates(scope, data.region, data.customLaborRateM2),
      edgeLengthM: edgesOnly ? data.edgeLengthM : undefined,
      pavingAreaM2: scope === 'paving' ? areaM2 : undefined,
      tilesAreaM2: scope === 'bathroom' ? areaM2 : undefined,
    },
  };
}

function buildQuickPhotoEstimate(data: {
  visions: VisionSuggestion[];
  manualAreaM2?: number;
  manualWorkingAreaM2?: number;
  manualAreaSource?: ManualAreaSource | null;
  manualHeightM?: number;
  surfaceCalculation?: SurfaceCalculationResult;
  manualScope: QuickScope;
  customScope?: string;
  customLaborRateM2?: number;
  edgeLengthM?: number;
  materialMode: QuickMaterialMode;
  materialDiscountPercent: number;
  materialClientPrice?: number;
  vatMode: QuickVatMode;
  contractorName?: string;
  contractorNip?: string;
  contractorContact?: string;
  region?: string;
}): QuickEstimateSummary {
  const mapped = buildEngineInput(data);
  const estimate = calculateQuickEstimate(mapped.input);
  const floorAreaM2 = data.manualAreaM2
    ? round1(data.manualAreaM2)
    : data.surfaceCalculation?.floorAreaM2
    ? round1(data.surfaceCalculation.floorAreaM2)
    : undefined;
  const workingAreaM2 = resolveQuickWorkingAreaM2(estimate.meta);
  const priceFrom = calculatePercentageMoney(estimate.cost, 0.9);
  const priceTo = calculatePercentageMoney(estimate.cost, 1.2);
  const hasVisionAnalysis = data.visions.length > 0;

  return {
    scope: mapped.scope,
    scopeLabel: mapped.scopeLabel,
    scopeSource: 'manual',
    areaM2: floorAreaM2 ?? workingAreaM2,
    floorAreaM2,
    workingAreaM2,
    floorAreaSource: mapped.areaSource,
    manualHeightM: data.manualHeightM,
    surfaceCalculation: data.surfaceCalculation,
    inputSourceLabel: getInputSourceLabel({ areaSource: mapped.areaSource, hasVisionAnalysis }),
    priceFrom,
    priceTo,
    confidence: mapped.confidence,
    hasRealConfidence: mapped.hasRealConfidence,
    isLocalMode: mapped.isLocalFallback,
    hasVisionAnalysis,
    visionSummary: buildVisionSummary({
      visions: data.visions,
      scope: mapped.scope,
      scopeLabel: mapped.scopeLabel,
    }),
    aiObservations: buildAiObservations(data.visions),
    risks: mapped.risks,
    engineInput: mapped.input,
    estimate,
    materialMode: data.materialMode,
    materialDiscountPercent: data.materialDiscountPercent,
    materialClientPrice: data.materialClientPrice,
    vatMode: data.vatMode,
    contractorName: data.contractorName?.trim() || undefined,
    contractorNip: data.contractorNip?.trim() || undefined,
    contractorContact: data.contractorContact?.trim() || undefined,
  };
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => reject(new Error('AI_TIMEOUT')), timeoutMs);
    promise
      .then(resolve)
      .catch(reject)
      .finally(() => window.clearTimeout(timeout));
  });
}

function buildMarginShieldAuditForResult(result: QuickEstimateSummary): MarginShieldAuditResult {
  return runMarginShieldAudit({
    curbLengthM: result.paving?.useMeasuredEdges ? result.paving.curbLengthM : undefined,
    region: result.engineInput.region,
    userRates: result.engineInput.userRates,
    workType: result.engineInput.tradeEngine ?? result.engineInput.trade ?? result.scope,
    description: [
      result.scopeLabel,
      result.engineInput.scopeText,
      ...result.aiObservations,
      ...result.risks,
    ].filter(Boolean).join(' '),
    items: result.estimate.items,
    area: result.estimate.meta.totalWorkingArea ?? result.workingAreaM2 ?? result.floorAreaM2,
  });
}

function buildQuickApprovedOffer(
  result: QuickEstimateSummary,
  decisions: Record<string, MarginShieldDecisionStatus>,
  manualItems: ManualEstimateItem[] = []
): ApprovedOfferViewModel {
  return buildApprovedOfferViewModel({
    estimate: result.estimate,
    marginShieldAudit: buildMarginShieldAuditForResult(result),
    workingAreaM2:
      result.workingAreaM2 ??
      result.estimate.meta.totalWorkingArea ??
      result.floorAreaM2 ??
      0,
    marginShieldDecisions: decisions,
    manualItems,
    materialMode: result.materialMode,
    materialClientPrice: result.materialClientPrice,
    settlementMode: getSettlementModeForVat(result.vatMode),
  });
}

function parseProjectSnapshot(value: unknown): QuickEstimateProjectSnapshot | null {
  if (!isUsableQuickProjectSnapshot(value)) return null;
  return readQuickEstimateSnapshot<
    QuickFormState,
    QuickEstimateSummary,
    ManualEstimateItem,
    MarginShieldDecisionStatus
  >(value);
}

export function QuickPhotoEstimate({ onClose, onAdvanced, onProjects, publicMode = false, initialProject, onNewEstimate, onProjectSaved }: QuickPhotoEstimateProps) {
  const projectSnapshot = React.useMemo(() => parseProjectSnapshot(initialProject?.quickEstimateSnapshot), [initialProject]);
  const reopenBlocked = Boolean(initialProject && !projectSnapshot);
  const savedProjectIdRef = useRef(initialProject?.id);
  const savePendingRef = useRef(false);
  const [isSavingProject, setIsSavingProject] = useState(false);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const profileDefaultsAppliedRef = useRef(false);
  const [photos, setPhotos] = useState<UploadedPhoto[]>([]);
  const { user, userProfile, addProject, updateProject } = useAppContext();
  // The local draft belongs to the detached (new) estimate of this device owner only.
  // A reopened project is restored from its Firestore snapshot and never touches it.
  const draftOwner = resolveQuickDraftOwner(user?.uid);
  const [initialDraft] = useState<LoadedQuickActiveDraft<QuickEstimateDraftForm> | null>(() =>
    initialProject ? null : loadQuickActiveDraft(getBrowserQuickDraftStorage(), draftOwner, sanitizeQuickEstimateDraftForm)
  );
  const draftSourceKeyRef = useRef<string | null>(initialDraft?.sourceKey ?? null);
  const [form, setForm] = useState<QuickFormState>(() => buildFormFromDraft(initialDraft?.form ?? {}));
  const [isManualMode, setIsManualMode] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [result, setResult] = useState<QuickEstimateSummary | null>(null);
  const [manualItems, setManualItems] = useState<ManualEstimateItem[]>(() => initialDraft?.manualItems ?? []);
  const [hasGeneratedPdf, setHasGeneratedPdf] = useState(false);
  const [isPdfDeliveryPending, setIsPdfDeliveryPending] = useState(false);
  const [marginShieldDecisions, setMarginShieldDecisions] = useState<Record<string, MarginShieldDecisionStatus>>(
    () => initialDraft?.marginShieldDecisions ?? {}
  );
  const [decisionScopeKey, setDecisionScopeKey] = useState<string | null>(() => initialDraft?.decisionScopeKey ?? null);
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [isListening, setIsListening] = useState(false);
  const [parsedVoiceCommand, setParsedVoiceCommand] = useState<ParsedVoiceCommand | null>(null);
  const [voiceStatus, setVoiceStatus] = useState<string | null>(null);
  const [visionSuggestions, setVisionSuggestions] = useState<VisionSuggestion[]>([]);
  const [isAnalyzingVision, setIsAnalyzingVision] = useState(false);
  const [visionStatusMessage, setVisionStatusMessage] = useState<VisionStatusMessage>(null);
  const [visionQuotaVersion, setVisionQuotaVersion] = useState(0);
  const [showWelcomeCard, setShowWelcomeCard] = useState(true);
  const [saveStatusMessage, setSaveStatusMessage] = useState<string | null>(null);
  const [pdfStatusMessage, setPdfStatusMessage] = useState<string | null>(null);
  const [pdfNoticeMessage, setPdfNoticeMessage] = useState<string | null>(null);
  const [draftStatusMessage, setDraftStatusMessage] = useState<string | null>(null);
  const isOnline = useOnlineStatus();
  const pwaInstall = usePwaInstall();

  const region = useProjectStore((state) => state.region);
  const setBranza = useProjectStore((state) => state.setBranza);
  const setProjectName = useProjectStore((state) => state.setProjectName);
  const setDescription = useProjectStore((state) => state.setDescription);
  const setGeometry = useProjectStore((state) => state.setGeometry);
  const setEstimate = useProjectStore((state) => state.setEstimate);
  const setMaterialMode = useProjectStore((state) => state.setMaterialMode);
  const setSettlementMode = useProjectStore((state) => state.setSettlementMode);
  const goTo = useProjectStore((state) => state.goTo);

  // TODO: replace FREE fallback with authenticated user plan from profile/claims.
  const aiPlan = resolveAIPlanFromRole(userProfile?.role, Boolean(import.meta.env.DEV));
  const visionQuotaStatus = React.useMemo(
    () => getAIQuotaStatus('vision', aiPlan),
    [aiPlan, visionQuotaVersion]
  );
  const hasAppVisionBypass = isAIQuotaUnlimited(visionQuotaStatus);
  const canCalculate = true;

  const updateForm = useCallback((patch: Partial<QuickFormState>) => {
    if ((patch.surfaceMode && patch.surfaceMode !== form.surfaceMode) ||
        (patch.pavingMode && patch.pavingMode !== form.pavingMode)) {
      setResult(null);
      setErrorMessage(null);
      setIsManualMode(false);
      setMarginShieldDecisions({});
    }
    setForm((current) => ({ ...current, ...patch }));
  }, [form.surfaceMode, form.pavingMode]);

  useEffect(() => {
    const snapshot = projectSnapshot;
    savedProjectIdRef.current = initialProject?.id;
    if (!snapshot) return;

    setForm(snapshot.form);
    setResult(snapshot.result);
    setManualItems(snapshot.manualItems);
    setMarginShieldDecisions(snapshot.marginShieldDecisions ?? {});
    setDecisionScopeKey(getDecisionScopeKey(snapshot.form));
    setSaveStatusMessage('Projekt otwarty. Możesz zmienić własne pozycje i ponownie wygenerować PDF.');
    setHasGeneratedPdf(false);
  }, [initialProject, projectSnapshot]);

  useEffect(() => {
    if (initialProject || !userProfile || profileDefaultsAppliedRef.current) return;
    profileDefaultsAppliedRef.current = true;

    setForm((current) => ({
      ...current,
      contractorName: current.contractorName || userProfile.companyName || '',
      contractorNip: current.contractorNip || userProfile.nip || '',
      contractorContact:
        current.contractorContact || userProfile.phone || userProfile.email || '',
      vatMode:
        current.vatMode === DEFAULT_FORM.vatMode
          ? userProfile.salesVatMode
          : current.vatMode,
      materialMode:
        current.materialMode === DEFAULT_FORM.materialMode
          ? userProfile.materialSide === 'contractor'
            ? 'contractor'
            : userProfile.materialSide === 'info'
              ? 'info'
              : 'client'
          : current.materialMode,
    }));
  }, [userProfile, initialProject]);

  useEffect(() => {
    if (initialProject) return;
    const timeoutId = window.setTimeout(() => {
      const saved = saveQuickActiveDraft(
        getBrowserQuickDraftStorage(),
        draftOwner,
        { form: pickQuickEstimateDraftForm(form), manualItems, marginShieldDecisions, decisionScopeKey },
        draftSourceKeyRef.current
      );
      if (saved) {
        draftSourceKeyRef.current = null;
        setDraftStatusMessage('Zapisano lokalnie na tym urządzeniu');
      }
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [initialProject, draftOwner, form, manualItems, marginShieldDecisions, decisionScopeKey]);

  const handleFiles = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files ?? []).slice(0, MAX_PHOTOS - photos.length);
    if (selected.length === 0) return;

    setPhotos((current) => [
      ...current,
      ...selected.map((file) => ({
        id: `${file.name}_${file.lastModified}_${Math.random().toString(36).slice(2)}`,
        file,
        previewUrl: URL.createObjectURL(file),
      })),
    ]);
    setVisionSuggestions([]);
    setVisionStatusMessage(null);
    event.target.value = '';
  }, [photos.length]);

  const removePhoto = useCallback((id: string) => {
    setPhotos((current) => {
      const removed = current.find((photo) => photo.id === id);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return current.filter((photo) => photo.id !== id);
    });
    setVisionSuggestions([]);
    setVisionStatusMessage(null);
  }, []);

  const syncResultToStore = useCallback((summary: QuickEstimateSummary, approvedItems: EngineResult['items']): void => {
    const branza: Branza =
      summary.engineInput.tradeEngine === 'bathroom'
        ? 'lazienka'
        : summary.engineInput.tradeEngine === 'paving'
          ? 'brukarstwo'
          : summary.engineInput.tradeEngine === 'general'
            ? 'ogolnobudowlany'
            : 'malarz';

    setBranza(branza);
    setProjectName('Szybka wycena ze zdjęć');
    setDescription(`Zakres: ${summary.scopeLabel}. Wycena orientacyjna do ręcznej weryfikacji.`);
    setGeometry({
      projectMode: summary.engineInput.projectMode ?? 'apartment',
      length: summary.engineInput.length ?? null,
      width: summary.engineInput.width ?? null,
      height: summary.engineInput.height ?? null,
      wysokosc: summary.engineInput.height ?? null,
      powPodlogi: summary.floorAreaM2 ?? summary.engineInput.floorArea ?? null,
      doorCount: summary.engineInput.doorCount ?? 0,
      bigWindowsArea: summary.engineInput.bigWindowsArea ?? 0,
      otworyDuzeM2: summary.engineInput.bigWindowsArea ?? 0,
      region: summary.engineInput.region ?? region,
    });
    setEstimate({
      ...summary.estimate,
      items: approvedItems,
      cost: sumMoney(approvedItems.map((item) => item.totalNetto)),
    });
  }, [region, setBranza, setDescription, setEstimate, setGeometry, setProjectName]);

  async function analyzePhotos(options?: { manualAreaM2?: number; updateState?: boolean }): Promise<VisionSuggestion[]> {
    const visions: VisionSuggestion[] = [];

    for (const photo of photos) {
      const imageBase64 = await fileToBase64(photo.file);
      const response = await withTimeout(
        analyzeRoomVision({
          imageBase64,
          mimeType: normalizePhotoMimeType(photo.file.type),
          trade: getPrimaryRateType(form.manualScope),
          scope: QUICK_SCOPE_LABELS[form.manualScope],
          floorAreaM2: options?.manualAreaM2,
          region,
        }),
        AI_TIMEOUTS_MS.vision
      );

      if ('offline' in response) {
        throw new Error(`${VISION_ERROR_PREFIX}${response.reason ?? ''}`);
      }
      visions.push(response);
    }

    if (options?.updateState) {
      setVisionSuggestions(visions);
      setVisionStatusMessage({
        tone: 'success',
        text: 'Zdjęcie pomaga ocenić zakres i stan prac. Metraż liczysz i zatwierdzasz Ty.',
      });
    }

    return visions;
  }

  async function handleAnalyzeVision(): Promise<void> {
    if (photos.length === 0 || isAnalyzingVision) return;

    if (!isOnline) {
      setVisionStatusMessage({
        tone: 'warning',
        text: 'Ta funkcja wymaga internetu. Robocza wycena została zapisana lokalnie.',
      });
      return;
    }

    setIsAnalyzingVision(true);
    setVisionStatusMessage(null);
    setErrorMessage(null);

    try {
      const quotaStatus = getAIQuotaStatus('vision', aiPlan);
      if (!quotaStatus.allowed) {
        setVisionStatusMessage({
          tone: 'warning',
          text: getVisionQuotaMessage('app_plan_limit'),
        });
        return;
      }

      const lengthM = parsePositiveNumber(form.roomLength);
      const widthM = parsePositiveNumber(form.roomWidth);
      const apartmentAreaM2 = parsePositiveNumber(form.apartmentArea);
      const floorAreaM2 =
        form.surfaceMode === 'apartment'
          ? apartmentAreaM2
          : form.surfaceMode === 'room' && lengthM && widthM
            ? round1(lengthM * widthM)
            : calculateSurfaceForQuickForm(form).finalWorkingAreaM2 || undefined;
      await analyzePhotos({ manualAreaM2: floorAreaM2, updateState: true });
      consumeAIQuota('vision', aiPlan);
      setVisionQuotaVersion((version) => version + 1);
    } catch (error) {
      setVisionSuggestions([]);
      setVisionStatusMessage({
        tone: 'warning',
        text: getVisionErrorMessage(error),
      });
    } finally {
      setIsAnalyzingVision(false);
    }
  }

  function updateVoiceTranscript(value: string): void {
    setVoiceTranscript(value);
    setVoiceStatus(null);
    setParsedVoiceCommand(value.trim() ? parseVoiceCommand(value) : null);
  }

  function handleVoiceInput(): void {
    const SpeechRecognition =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setVoiceError('Dyktowanie niedostępne w tej przeglądarce. Wpisz zmianę ręcznie.');
      return;
    }

    setVoiceError(null);
    setVoiceStatus(null);
    setIsListening(true);

    const recognition = new SpeechRecognition();
    recognition.lang = 'pl-PL';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event: any) => {
      const transcript = String(event.results?.[0]?.[0]?.transcript ?? '');
      updateVoiceTranscript(transcript);
    };
    recognition.onerror = () => {
      setVoiceError('Nie udało się rozpoznać głosu. Wpisz zmianę ręcznie.');
    };
    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.start();
  }

  function applyVoiceCommand(): void {
    if (!parsedVoiceCommand || parsedVoiceCommand.status === 'uncertain') {
      setVoiceStatus('Nie jestem pewien. Popraw tekst ręcznie.');
      return;
    }

    if (parsedVoiceCommand.action === 'update_area') {
      // An area note is not a scope change: decisions are reconciled at the next calculation.
      setIsManualMode(true);
      setResult(null);
      setVoiceStatus(`W Quick Estimate wpisz długość i szerokość pokoju. Rozpoznano metraż ${parsedVoiceCommand.areaM2} m² jako notatkę pomocniczą.`);
      return;
    }

    if (parsedVoiceCommand.action === 'toggle_priming') {
      updateForm({ manualScope: 'scrape_prime_plaster_paint' });
      setResult(null);
      setMarginShieldDecisions({});
      setVoiceStatus('Dodano gruntowanie do zakresu. Kliknij Policz wycenę.');
      return;
    }

    if (parsedVoiceCommand.action === 'set_vat') {
      setSettlementMode(parsedVoiceCommand.settlementMode);
      setVoiceStatus(`Ustawiono ${parsedVoiceCommand.label}.`);
      return;
    }

    if (parsedVoiceCommand.action === 'set_material_mode') {
      setMaterialMode(parsedVoiceCommand.materialMode);
      updateForm({ materialMode: fromDomainMaterialMode(parsedVoiceCommand.materialMode) });
      setVoiceStatus(`Ustawiono ${parsedVoiceCommand.label}.`);
      return;
    }

    const addition = `${parsedVoiceCommand.itemName}${parsedVoiceCommand.quantity ? ` ${parsedVoiceCommand.quantity}` : ''}${parsedVoiceCommand.unit ? ` ${parsedVoiceCommand.unit}` : ''}`;
    updateForm({
      manualScope: 'other',
      customScope: [form.customScope, addition].filter(Boolean).join('\n'),
    });
    setResult(null);
    setMarginShieldDecisions({});
    setVoiceStatus('Dopisano tekst do opisu. Sprawdź i kliknij Policz wycenę.');
  }

  async function handleCalculate(): Promise<void> {
    setIsCalculating(true);
    setErrorMessage(null);
    trackProductEvent('estimate_started');

    try {
      if (form.manualScope === 'paving') {
        const edgesOnly = form.pavingMode === 'edges_only';
        const pavingArea = edgesOnly ? undefined : parsePositiveNumber(form.pavingArea);
        const pavingLength = edgesOnly ? undefined : parsePositiveNumber(form.pavingLength);
        const pavingWidth = edgesOnly ? undefined : parsePositiveNumber(form.pavingWidth);
        const customLaborRateM2 =
          form.laborRateMode === 'custom' ? parsePositiveNumber(form.customLaborRate) : undefined;
        const pavingValidation = validateQuickEstimateForm({
          manualScope: 'paving',
          pavingMode: form.pavingMode,
          pavingAreaM2: pavingArea,
          pavingLengthM: pavingLength,
          pavingWidthM: pavingWidth,
          curbLengthM: parsePositiveNumber(form.curbLength),
          customLaborRateRaw: form.customLaborRate,
          customLaborRateM2,
          laborRateMode: form.laborRateMode,
        });
        if (pavingValidation) {
          setIsManualMode(true);
          setErrorMessage(pavingValidation);
          return;
        }
        const pavingWorkingAreaM2 = computePavingWorkingAreaM2(pavingArea, pavingLength, pavingWidth);
        const materialDiscountPercent = parseNonNegativeNumber(form.materialDiscountPercent) ?? 0;
        const materialClientPrice = parseNonNegativeNumber(form.materialClientPrice);
        const summary = buildQuickPhotoEstimate({
          visions: visionSuggestions,
          manualAreaM2: pavingWorkingAreaM2,
          manualAreaSource: 'manual',
          manualScope: 'paving',
          edgeLengthM: parsePositiveNumber(form.curbLength),
          customScope: form.customScope,
          customLaborRateM2,
          materialMode: form.materialMode,
          materialDiscountPercent,
          materialClientPrice,
          vatMode: form.vatMode,
          contractorName: form.contractorName,
          contractorNip: form.contractorNip,
          contractorContact: form.contractorContact,
          region,
        });
        const pavingSummary: QuickEstimateSummary = {
          ...summary,
          paving: {
            workingAreaM2: pavingWorkingAreaM2,
            curbLengthM: parsePositiveNumber(form.curbLength),
            useMeasuredEdges: true,
            thicknessLabel: pavingWorkingAreaM2 > 0 ? PAVING_THICKNESS_LABELS[form.pavingThickness] : undefined,
            subbaseLabel: pavingWorkingAreaM2 > 0 ? PAVING_SUBBASE_LABELS[form.pavingSubbase] : undefined,
            transportLabel: PAVING_TRANSPORT_LABELS[form.pavingTransport],
          },
        };
        setResult(pavingSummary);
        reconcileDecisionsForResult(pavingSummary);
        setSaveStatusMessage(null);
        setIsManualMode(false);
        trackProductEvent('estimate_calculated');
        return;
      }

      const readyWorkingAreaM2 = parsePositiveNumber(form.readyWorkingArea);
      const manualHeightM = parsePositiveNumber(form.manualHeight);
      const roomLengthM = parsePositiveNumber(form.roomLength);
      const roomWidthM = parsePositiveNumber(form.roomWidth);
      const doorCount = parseNonNegativeInteger(form.doorCount);
      const largeOpeningsAreaM2 = parseNonNegativeNumber(form.largeOpeningsArea);
      const apartmentAreaM2 = parsePositiveNumber(form.apartmentArea);
      const apartmentHeightM = parsePositiveNumber(form.apartmentHeight) ?? 2.65;
      const apartmentDoorCount = parseNonNegativeInteger(form.apartmentDoorCount);
      const apartmentLargeOpeningsAreaM2 = parseNonNegativeNumber(form.apartmentLargeOpeningsArea);
      const bathroomAreaM2 = parseNonNegativeNumber(form.bathroomArea);
      const surfaceCalculation = calculateSurfaceForQuickForm(form);
      const areaInputs = resolveQuickAreaInputs(surfaceCalculation);
      const effectiveManualAreaM2 = areaInputs.manualAreaM2;
      const effectiveWorkingAreaM2 = areaInputs.workingAreaM2;
      const customLaborRateM2 =
        form.laborRateMode === 'custom' ? parsePositiveNumber(form.customLaborRate) : undefined;
      const materialDiscountPercent = parseNonNegativeNumber(form.materialDiscountPercent) ?? 0;
      const materialClientPrice = parseNonNegativeNumber(form.materialClientPrice);
      const validationMessage = validateQuickEstimateForm({
        surfaceMode: form.surfaceMode,
        readyWorkingAreaRaw: form.readyWorkingArea,
        readyWorkingAreaM2,
        manualScope: form.manualScope,
        customScope: form.customScope,
        manualHeightRaw: form.manualHeight,
        manualHeightM,
        roomLengthRaw: form.roomLength,
        roomLengthM,
        roomWidthRaw: form.roomWidth,
        roomWidthM,
        doorCountRaw: form.doorCount,
        doorCount,
        largeOpeningsAreaRaw: form.largeOpeningsArea,
        largeOpeningsAreaM2,
        apartmentAreaRaw: form.apartmentArea,
        apartmentAreaM2,
        apartmentHeightRaw: form.apartmentHeight,
        apartmentHeightM,
        apartmentDoorCountRaw: form.apartmentDoorCount,
        apartmentDoorCount,
        apartmentLargeOpeningsAreaRaw: form.apartmentLargeOpeningsArea,
        apartmentLargeOpeningsAreaM2,
        bathroomAreaRaw: form.bathroomArea,
        bathroomAreaM2,
        customLaborRateRaw: form.customLaborRate,
        customLaborRateM2,
        laborRateMode: form.laborRateMode,
      });

      if (validationMessage) {
        setErrorMessage(validationMessage);
        return;
      }

      let visions: VisionSuggestion[] = visionSuggestions;

      if (photos.length > 0 && visions.length === 0) {
        const quotaStatus = getAIQuotaStatus('vision', aiPlan);
        if (!isOnline) {
          setVisionStatusMessage({
            tone: 'warning',
            text: 'Ta funkcja wymaga internetu. Robocza wycena została zapisana lokalnie.',
          });
          visions = [];
        } else if (!quotaStatus.allowed) {
          setVisionStatusMessage({
            tone: 'warning',
            text: getVisionQuotaMessage('app_plan_limit'),
          });
          visions = [];
        } else {
          try {
            visions = await analyzePhotos({ manualAreaM2: effectiveManualAreaM2 });
            setVisionSuggestions(visions);
            consumeAIQuota('vision', aiPlan);
            setVisionQuotaVersion((version) => version + 1);
          } catch (error) {
            setVisionStatusMessage({
              tone: 'warning',
              text: getVisionErrorMessage(error),
            });
            if (!effectiveManualAreaM2 && !effectiveWorkingAreaM2) throw new Error('VISION_UNAVAILABLE');
            visions = [];
          }
        }
      }

      if (!effectiveManualAreaM2 && !effectiveWorkingAreaM2 && visions.length === 0) {
        setIsManualMode(true);
        setErrorMessage('Podaj powierzchnię roboczą albo wymiary do obmiaru.');
        return;
      }

      const summary = buildQuickPhotoEstimate({
        visions,
        manualAreaM2: effectiveManualAreaM2,
        manualWorkingAreaM2: areaInputs.engineManualWorkingAreaM2,
        manualAreaSource: 'manual',
        manualHeightM: areaInputs.heightM,
        surfaceCalculation,
        manualScope: form.manualScope,
        customScope: form.customScope,
        customLaborRateM2,
        materialMode: form.materialMode,
        materialDiscountPercent,
        materialClientPrice,
        vatMode: form.vatMode,
        contractorName: form.contractorName,
        contractorNip: form.contractorNip,
        contractorContact: form.contractorContact,
        region,
      });

      setResult(summary);
      reconcileDecisionsForResult(summary);
      setSaveStatusMessage(null);
      setIsManualMode(false);
      trackProductEvent('estimate_calculated');
    } catch {
      setIsManualMode(true);
      setErrorMessage(VISION_MANUAL_DIMENSIONS_MESSAGE);
    } finally {
      setIsCalculating(false);
    }
  }

  function handleManualCorrection(): void {
    // Hiding the result to correct data keeps decisions; the next calculation
    // decides whether they still apply (same scope) or are dropped.
    setResult(null);
    setSaveStatusMessage(null);
  }

  function handleNewEstimate(): void {
    const hasWork = Boolean(result) || manualItems.length > 0 || Object.keys(marginShieldDecisions).length > 0;
    if (
      hasWork &&
      !window.confirm(
        'Rozpocząć nową, pustą wycenę? Bieżące dane tej wyceny zostaną usunięte z tego urządzenia. Zapisane projekty pozostają bez zmian.'
      )
    ) {
      return;
    }
    onNewEstimate?.();
  }

  function reconcileDecisionsForResult(summary: QuickEstimateSummary): void {
    const nextScopeKey = getDecisionScopeKey(form);
    const availableAlertIds = buildMarginShieldAuditForResult(summary).alerts.map((alert) => alert.id);
    setMarginShieldDecisions((current) =>
      reconcileMarginShieldDecisions({ decisions: current, decisionScopeKey, nextScopeKey, availableAlertIds })
    );
    setDecisionScopeKey(nextScopeKey);
  }

  function handleAddManualItem(input: ManualItemInput): void {
    const id = `manual_${String(Date.now())}_${Math.random().toString(36).slice(2, 8)}`;
    setManualItems((current) => [...current, createManualEstimateItem(id, input)]);
    setSaveStatusMessage(null);
  }

  function handleUpdateManualItem(id: string, input: ManualItemInput): void {
    setManualItems((current) =>
      current.map((item) => (item.id === id ? updateManualEstimateItem(item, input) : item))
    );
    setSaveStatusMessage(null);
  }

  function handleRemoveManualItem(id: string): void {
    setManualItems((current) => removeManualEstimateItem(current, id));
    setSaveStatusMessage(null);
  }

  async function handleSaveProject(): Promise<void> {
    if (!result || reopenBlocked) return;
    if (!user) {
      setSaveStatusMessage('Zaloguj się, aby zapisać projekt i wrócić do niego później.');
      return;
    }
    if (!isOnline) {
      setSaveStatusMessage('Ta funkcja wymaga internetu. Robocza wycena została zapisana lokalnie.');
      return;
    }
    if (!canStartQuickProjectSave({
      hasResult: true,
      reopenBlocked: false,
      isAuthenticated: true,
      isOnline: true,
      lock: savePendingRef,
      onMessage: setSaveStatusMessage,
    })) return;

    setIsSavingProject(true);
    try {
      const approvedOffer = buildQuickApprovedOffer(result, marginShieldDecisions, manualItems);
      syncResultToStore(result, approvedOffer.items);
      const settlementMode = getSettlementModeForVat(result.vatMode);
      const estimatedAreaM2 = resolveQuickEstimatedAreaM2({
        workingAreaM2: result.workingAreaM2,
        estimateMeta: result.estimate.meta,
      });

      const projectData = {
        name: `Szybka wycena - ${result.scopeLabel}`,
        clientName: '',
        date: new Date().toISOString().slice(0, 10),
        trade: result.engineInput.trade ?? 'malarz',
        details: `Zakres: ${result.scopeLabel}. Powierzchnia do wyceny: ${String(estimatedAreaM2)} m².`,
        materials: [],
        laborItems: approvedOffer.items.map((item) => ({
          id: item.id,
          name: item.label,
          quantity: item.quantity,
          unit: item.unit,
          unitPrice: item.unitRateNetto,
          ...(item.itemType ? { itemType: item.itemType } : {}),
          ...(item.source ? { source: item.source } : {}),
        })),
        totalBaseCost: approvedOffer.pricing.netTotal,
        totalDiscountedCost: approvedOffer.pricing.grossTotal,
        laborCost: approvedOffer.pricing.workNet,
        totalNetto: approvedOffer.pricing.netTotal,
        totalBrutto: approvedOffer.pricing.grossTotal,
        area: estimatedAreaM2,
        estimatedAreaM2,
        voivodeship: region,
        vatRate: approvedOffer.pricing.vatRate * 100,
        settlementMode,
        quickEstimateSnapshot: createQuickEstimateSnapshot({
          approvedOffer: buildApprovedOfferDocument(approvedOffer, result.vatMode),
          form,
          result,
          manualItems,
          marginShieldDecisions,
        }),
      };

      savedProjectIdRef.current = await persistQuickProject({
        id: savedProjectIdRef.current,
        payload: projectData,
        isValid: (payload) => isUsableQuickProjectSnapshot(payload.quickEstimateSnapshot),
        update: (id, payload) => updateProject(id, buildQuickProjectUpdate(payload)),
        create: addProject,
      });
      onProjectSaved?.(savedProjectIdRef.current);

      setSaveStatusMessage('Projekt zapisany. Możesz wrócić do niego w Moich projektach.');
    } catch {
      setSaveStatusMessage('Nie udało się zapisać projektu. Spróbuj ponownie po chwili.');
    } finally {
      savePendingRef.current = false;
      setIsSavingProject(false);
    }
  }

  async function handlePdf(): Promise<void> {
    if (!result || isPdfDeliveryPending) return;

    if (result.vatMode === 'confirm') {
      setPdfStatusMessage('Wybierz: bez VAT, VAT 8% albo VAT 23%. PDF nie został utworzony.');
      trackProductEvent('pdf_failed');
      void logOperationalError('pdf', 'unresolved_vat_mode');
      return;
    }

    const approvedOffer = buildQuickApprovedOffer(result, marginShieldDecisions, manualItems);
    const contactPatch = getPdfContactPatch(
      form.contractorContact || result.contractorContact || ''
    );
    const contractor = {
      companyName: form.contractorName || result.contractorName || userProfile?.companyName,
      nip: form.contractorNip || result.contractorNip || userProfile?.nip,
      phone: contactPatch.phone || userProfile?.phone,
      email: contactPatch.email || userProfile?.email,
      isPro: userProfile?.role === 'PRO' || userProfile?.status === 'paid',
    };
    // An incomplete profile never blocks the estimate — it decides which
    // document the contractor gets. Logged out, or logged in without a finished
    // profile, produces the test draft; a complete profile produces the offer.
    const documentVariant = resolveOfferDocumentVariant({
      isLoggedIn: Boolean(user),
      contractor,
    });

    setPdfStatusMessage(null);
    setPdfNoticeMessage(
      documentVariant === 'test' ? getTestVariantNotice(Boolean(user)) : null
    );
    syncResultToStore(result, approvedOffer.items);
    let artifact: ReturnType<typeof createClientOfferPdfArtifact>;
    try {
      artifact = createClientOfferPdfArtifact({
      documentVariant,
      clientName: '',
      projectName: 'Szybka wycena',
      scopeLabel: result.scopeLabel,
      sourceLabel: 'wycena robocza do potwierdzenia',
      contractor,
      date: new Date(),
      floorAreaM2: result.floorAreaM2,
      estimatedAreaM2: resolveQuickEstimatedAreaM2({
        workingAreaM2: result.workingAreaM2,
        estimateMeta: result.estimate.meta,
      }),
      surfaceSummary: result.paving
        ? {
            paving: true,
            pavingWorkingAreaM2: result.paving.workingAreaM2,
            curbLengthM: result.paving.curbLengthM,
            pavingThicknessLabel: result.paving.thicknessLabel,
            pavingSubbaseLabel: result.paving.subbaseLabel,
            pavingTransportLabel: result.paving.transportLabel,
            confirmationStatus: 'wycena robocza do potwierdzenia',
          }
        : buildQuickOfferSurfaceSummary({
            surfaceCalculation: result.surfaceCalculation,
            estimateMeta: result.estimate.meta,
          }),
      ...buildApprovedOfferDocument(approvedOffer, result.vatMode),
      });
    } catch {
      setPdfStatusMessage('Nie udało się utworzyć PDF. Dane wyceny pozostały bez zmian.');
      trackProductEvent('pdf_failed');
      void logOperationalError('pdf', 'pdf_generation_failed');
      return;
    }

    // Readiness is decided by the delivery outcome below, not by document creation.
    // This event means that the PDF document was created; delivery is a separate outcome.
    trackProductEvent('pdf_generated');
    setIsPdfDeliveryPending(true);
    try {
      const delivery = await deliverPdfArtifact(artifact);
      if (delivery.status === 'delivered') {
        setHasGeneratedPdf(true);
      } else if (delivery.status === 'blocked') {
        setPdfStatusMessage(FACEBOOK_INSTAGRAM_PDF_MESSAGE);
      } else if (delivery.status === 'cancelled') {
        setPdfStatusMessage(PDF_SHARE_CANCELLED_MESSAGE);
      } else if (delivery.status === 'failed') {
        setPdfStatusMessage(PDF_DELIVERY_FAILED_MESSAGE);
      }
    } finally {
      setIsPdfDeliveryPending(false);
    }
  }
  if (reopenBlocked) {
    return (
      <div className="min-h-screen bg-[#0b0d10] px-4 py-8 text-white">
        <div role="alert" className="mx-auto max-w-xl rounded-2xl border border-amber-400/40 bg-[#15191f] p-6">
          <h1 className="text-xl font-bold">Nie można bezpiecznie otworzyć tej wyceny</h1>
          <p className="mt-3 text-slate-300">Projekt nie ma poprawnego zapisu szybkiej wyceny lub używa nieobsługiwanej wersji. Edycja i zapis są zablokowane, aby chronić istniejące dane.</p>
          <button type="button" onClick={onProjects ?? onClose} className="mt-5 min-h-14 rounded-xl bg-amber-400 px-5 font-bold text-black">Wróć do projektów</button>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen overflow-x-hidden bg-[#0b0d10] text-white">
      <main className="mx-auto max-w-[1160px] px-4 py-5 pb-24 sm:px-6 lg:py-8">
        <Header onClose={onClose} publicMode={publicMode} />
        {publicMode && showWelcomeCard && (
          <PublicQuickEstimateNotice
            onLogin={onAdvanced}
            onClose={() => setShowWelcomeCard(false)}
          />
        )}
        <div className="mb-5 grid gap-3">
          {onNewEstimate && (
            <button
              type="button"
              onClick={handleNewEstimate}
              className="h-14 w-full rounded-2xl border border-[#2a2f38] bg-[#15191f] px-5 text-base font-black text-slate-100 transition hover:border-amber-400/40 hover:text-amber-200 sm:w-auto sm:justify-self-start"
            >
              Nowa wycena
            </button>
          )}
          {draftStatusMessage && (
            <p className="rounded-2xl border border-[#2a2f38] bg-[#15191f] px-4 py-3 text-sm font-semibold text-slate-300">
              {draftStatusMessage}
            </p>
          )}
          {!isOnline && (
            <p className="rounded-2xl border border-amber-400/30 bg-amber-500/10 px-4 py-3 text-sm font-semibold text-amber-100">
              {OFFLINE_NETWORK_FEATURE_MESSAGE}
            </p>
          )}
        </div>
        <ScopeIntakeCard key={initialProject?.id ?? 'new'} />
        <div className="grid gap-5 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
          <div className="space-y-5">
            <PhotoPickerCard
              cameraInputRef={cameraInputRef}
              galleryInputRef={galleryInputRef}
              photos={photos}
              isBusy={isCalculating}
              isAnalyzingVision={isAnalyzingVision}
              visionStatusMessage={visionStatusMessage}
              visionQuotaLabel={getVisionQuotaLabel(visionQuotaStatus.allowed, hasAppVisionBypass)}
              hasVisionResult={visionSuggestions.length > 0}
              onTakePhoto={() => cameraInputRef.current?.click()}
              onChooseGallery={() => galleryInputRef.current?.click()}
              onAnalyzeVision={handleAnalyzeVision}
              onFiles={handleFiles}
              onRemove={removePhoto}
            />
            {visionSuggestions.length > 0 && (
              <VisionAnalysisCard vision={visionSuggestions[0]} />
            )}
            <VoiceCorrectionCard
              transcript={voiceTranscript}
              isListening={isListening}
              errorMessage={voiceError}
              statusMessage={voiceStatus}
              parsedCommand={parsedVoiceCommand}
              onStart={handleVoiceInput}
              onTranscriptChange={updateVoiceTranscript}
              onApply={applyVoiceCommand}
            />
          </div>
          <div className="space-y-5">
            <SimpleEstimateCard
              form={form}
              region={region}
              isManualMode={isManualMode || photos.length === 0}
              isCalculating={isCalculating}
              errorMessage={errorMessage}
              canCalculate={canCalculate}
              onChange={updateForm}
              onCalculate={handleCalculate}
            />
          </div>
        </div>
        {result && (
            <ResultCard
              result={result}
              publicMode={publicMode}
              userLabel={user?.email ?? user?.displayName ?? undefined}
              saveStatusMessage={saveStatusMessage}
              pdfStatusMessage={pdfStatusMessage}
              pdfNoticeMessage={pdfNoticeMessage}
              hasGeneratedPdf={hasGeneratedPdf}
              isPdfDeliveryPending={isPdfDeliveryPending}
              pwaInstall={pwaInstall}
              manualItems={manualItems}
              marginShieldDecisions={marginShieldDecisions}
              onEdit={handleManualCorrection}
              onLogin={onAdvanced}
              onPdf={handlePdf}
              onSaveProject={handleSaveProject}
              isSavingProject={isSavingProject}
              onProjects={onProjects ?? onAdvanced}
              onAddManualItem={handleAddManualItem}
              onUpdateManualItem={handleUpdateManualItem}
              onRemoveManualItem={handleRemoveManualItem}
              onMarginShieldDecisionChange={(alertId, status) => {
                trackProductEvent('shield_reviewed');
                setMarginShieldDecisions((current) =>
                  setMarginShieldDecision(current, alertId, status)
                );
              }}
          />
        )}
      </main>
    </div>
  );
}

const Header = memo(function Header({ onClose, publicMode }: { onClose?: () => void; publicMode: boolean }) {
  return (
    <header className="mb-5 rounded-[30px] border border-[#2a2f38] bg-[#15191f] p-5 shadow-2xl shadow-black/30 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <p className="text-xs font-black uppercase tracking-[0.26em] text-amber-300">ProWycena Core</p>
        <h1 className="mt-2 text-4xl font-black tracking-tight text-white sm:text-5xl">Szybka wycena</h1>
        <p className="mt-2 max-w-xl text-sm font-semibold text-slate-400">
          {publicMode ? 'Zdjęcia, prosty metraż, orientacyjny wynik i roboczy PDF testowy.' : 'Zdjęcia, prosty metraż, wynik i PDF.'}
        </p>
        <CoreStatusBadgeRow className="mt-3">
          <CoreStatusBadge label="Zdjęcie: kontekst" tone="info" />
          <CoreStatusBadge label="Do potwierdzenia" tone="muted" />
          <CoreStatusBadge label="PDF" tone="success" />
        </CoreStatusBadgeRow>
      </div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="min-h-12 rounded-2xl border border-[#2a2f38] bg-[#0f1217] px-4 text-sm font-bold text-slate-300 transition hover:border-amber-400/40 hover:text-amber-200"
        >
          Wróć do strony głównej
        </button>
      )}
      </div>
    </header>
  );
});

const PublicQuickEstimateNotice = memo(function PublicQuickEstimateNotice({
  onLogin,
  onClose,
}: {
  onLogin: () => void;
  onClose: () => void;
}) {
  return (
    <section className="mb-5 rounded-2xl border border-cyan-400/30 bg-cyan-500/10 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-black text-cyan-100">Tryb bez logowania</p>
          <p className="mt-1 text-sm text-slate-300">
            Możesz policzyć szybką wycenę. Zaloguj się, aby zapisać projekt.
          </p>
        </div>
        <div className="grid gap-2 sm:flex sm:items-center">
          <button
            type="button"
            onClick={onClose}
            className="min-h-12 rounded-2xl border border-[#2a2f38] bg-[#0f1217] px-4 text-sm font-bold text-slate-200 transition hover:border-cyan-400/40 hover:text-cyan-100"
          >
            Ukryj informację
          </button>
          <button
            type="button"
            onClick={onLogin}
            className="min-h-12 rounded-2xl border border-cyan-400/40 bg-[#0f1217] px-4 text-sm font-black text-cyan-100 transition hover:bg-cyan-500/10"
          >
            Zaloguj się
          </button>
        </div>
      </div>
    </section>
  );
});

const PhotoPickerCard = memo(function PhotoPickerCard({
  cameraInputRef,
  galleryInputRef,
  photos,
  isBusy,
  isAnalyzingVision,
  visionStatusMessage,
  visionQuotaLabel,
  hasVisionResult,
  onTakePhoto,
  onChooseGallery,
  onAnalyzeVision,
  onFiles,
  onRemove,
}: {
  cameraInputRef: React.RefObject<HTMLInputElement | null>;
  galleryInputRef: React.RefObject<HTMLInputElement | null>;
  photos: UploadedPhoto[];
  isBusy: boolean;
  isAnalyzingVision: boolean;
  visionStatusMessage: VisionStatusMessage;
  visionQuotaLabel: string;
  hasVisionResult: boolean;
  onTakePhoto: () => void;
  onChooseGallery: () => void;
  onAnalyzeVision: () => void;
  onFiles: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove: (id: string) => void;
}) {
  const disabled = isBusy || isAnalyzingVision || photos.length >= MAX_PHOTOS;
  const canAnalyze = photos.length > 0 && !isBusy && !isAnalyzingVision;

  return (
    <section className="rounded-[28px] border border-[#2a2f38] bg-[#15191f] p-5 shadow-xl shadow-black/25">
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={onFiles} />
      <input ref={galleryInputRef} type="file" accept="image/*" multiple className="hidden" onChange={onFiles} />
      <div className="mb-4">
          <p className="text-xs font-black uppercase tracking-[0.22em] text-cyan-200">Zdjęcie opcjonalne</p>
        <h2 className="mt-2 text-2xl font-black text-white">Dodaj zdjęcie</h2>
        <p className="mt-1 text-sm text-slate-400">
          Zdjęcie pomaga ocenić zakres i stan prac. Metraż liczysz i zatwierdzasz Ty.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <CoreStatusBadge label={visionQuotaLabel} tone={visionQuotaLabel === 'Tryb ręczny' ? 'muted' : 'info'} />
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <button
          type="button"
          disabled={disabled}
          onClick={onTakePhoto}
          className="flex min-h-16 w-full items-center justify-center gap-3 rounded-2xl border border-dashed border-amber-400/50 bg-amber-500/10 px-4 text-base font-bold text-amber-200 transition hover:bg-amber-500/15 disabled:opacity-50"
        >
          <Camera className="h-5 w-5" />
          Zrób zdjęcie aparatem
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={onChooseGallery}
          className="flex min-h-16 w-full items-center justify-center gap-3 rounded-2xl border border-[#2a2f38] bg-[#0f1217] px-4 text-base font-bold text-slate-200 transition hover:border-amber-400/40 disabled:opacity-50"
        >
          <Upload className="h-5 w-5" />
          Wybierz z galerii / pliku
        </button>
      </div>
      <div className="mt-2 text-right text-sm font-semibold text-amber-200">{photos.length}/{MAX_PHOTOS}</div>
      {photos.length > 0 && (
        <>
          <div className="mt-3 grid grid-cols-5 gap-2">
            {photos.map((photo) => (
              <div key={photo.id} className="relative aspect-square overflow-hidden rounded-2xl border border-[#2a2f38]">
                <img src={photo.previewUrl} alt="Zdjęcie do wyceny" className="h-full w-full object-cover" />
                <button type="button" onClick={() => onRemove(photo.id)} className="absolute right-1 top-1 grid h-7 w-7 place-items-center rounded-full bg-slate-900/70 text-white">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            disabled={!canAnalyze}
            onClick={onAnalyzeVision}
            className="mt-4 flex min-h-14 w-full items-center justify-center gap-2 rounded-2xl border border-cyan-400/40 bg-cyan-500/10 px-4 text-base font-black text-cyan-100 transition hover:bg-cyan-500/15 disabled:opacity-50"
          >
            {isAnalyzingVision ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />}
            {isAnalyzingVision ? 'Analizuję zdjęcie...' : hasVisionResult ? 'Analizuj zdjęcie ponownie' : 'Analizuj zdjęcie'}
          </button>
        </>
      )}
      {visionStatusMessage && (
        <div
          className={`mt-3 rounded-lg border px-3 py-2 text-sm font-semibold ${
            visionStatusMessage.tone === 'success'
              ? 'border-cyan-400/30 bg-cyan-500/10 text-cyan-100'
              : 'border-amber-400/30 bg-amber-500/10 text-amber-100'
          }`}
        >
          {visionStatusMessage.text}
        </div>
      )}
    </section>
  );
});

const VisionAnalysisCard = memo(function VisionAnalysisCard({ vision }: { vision: VisionSuggestion }) {
  const observations = [...new Set([...(vision.observations ?? []), ...(vision.notes ?? [])])]
    .map(translateVisionText)
    .slice(0, 5);
  const warnings = [...new Set(vision.warnings ?? [])].map(translateVisionText).slice(0, 4);
  const confirmations = [...new Set(vision.requiresManualConfirmation ?? [])].slice(0, 5);
  const confidence = typeof vision.confidence === 'number' ? `${clamp(Math.round(vision.confidence), 0, 100)}%` : 'do potwierdzenia';
  const surfaceCondition = getSurfaceConditionLabel([vision]);

  return (
    <section className="rounded-[28px] border border-cyan-400/30 bg-[#15191f] p-5 shadow-xl shadow-black/25">
      <CoreStatusBadgeRow>
        <CoreStatusBadge label="Zdjęcie" tone="info" />
        <CoreStatusBadge label="Do weryfikacji" tone="amber" />
      </CoreStatusBadgeRow>
      <div className="mt-3">
        <p className="text-xs font-black uppercase tracking-[0.22em] text-cyan-200">Analiza zdjęcia</p>
        <h2 className="mt-2 text-2xl font-black text-white">Co widzi AI</h2>
      </div>
      <div className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
        <EstimateFieldRow label="Typ pomieszczenia" value={getRoomTypeLabel(vision.roomType)} source="vision" />
        <EstimateFieldRow label="Widoczne ściany" value={String(vision.visibleWalls ?? 0)} source="vision" />
        <EstimateFieldRow label="Drzwi" value={String(vision.doorCount ?? 0)} source="vision" />
        <EstimateFieldRow label="Okna" value={String(vision.standardWindowCount ?? 0)} source="vision" />
        <EstimateFieldRow label="Gniazdka" value={String(vision.sockets ?? 0)} source="vision" />
        <EstimateFieldRow label="Pewność" value={confidence} source="vision" />
        {surfaceCondition && (
          <EstimateFieldRow label="Stan podłoża" value={surfaceCondition.value} source="vision" />
        )}
        <EstimateFieldRow
          label="Sugerowany zakres"
          value={(vision.suggestedScope ?? ['inne']).join(', ')}
          source="confirm"
        />
      </div>
      {observations.length > 0 && (
        <div className="mt-4">
          <p className="text-sm font-semibold text-white">Obserwacje</p>
          <ul className="mt-2 grid gap-2 text-sm text-slate-300">
            {observations.map((item) => (
              <li key={item} className="rounded-lg border border-[#2a2f38] bg-[#0f1217] px-3 py-2">{item}</li>
            ))}
          </ul>
        </div>
      )}
      {warnings.length > 0 && (
        <div className="mt-4">
          <p className="text-sm font-semibold text-white">Ostrzeżenia</p>
          <ul className="mt-2 grid gap-2 text-sm text-amber-100">
            {warnings.map((item) => (
              <li key={item} className="rounded-lg border border-amber-400/20 bg-amber-500/10 px-3 py-2">{item}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="mt-4">
        <p className="text-sm font-semibold text-white">Brakujące dane do ręcznego wpisania</p>
        <ul className="mt-2 grid gap-2 text-sm text-slate-300">
          {(confirmations.length > 0 ? confirmations : ['metraż ścian i sufitu', 'gruntowanie', 'okna i drzwi']).map((item) => (
            <li key={item} className="rounded-lg border border-[#2a2f38] bg-[#0f1217] px-3 py-2">{item}</li>
          ))}
        </ul>
      </div>
    </section>
  );
});

const VoiceCorrectionCard = memo(function VoiceCorrectionCard({
  transcript,
  isListening,
  errorMessage,
  statusMessage,
  parsedCommand,
  onStart,
  onTranscriptChange,
  onApply,
}: {
  transcript: string;
  isListening: boolean;
  errorMessage: string | null;
  statusMessage: string | null;
  parsedCommand: ParsedVoiceCommand | null;
  onStart: () => void;
  onTranscriptChange: (value: string) => void;
  onApply: () => void;
}) {
  const canApply = parsedCommand?.status === 'parsed';
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <section className="rounded-[28px] border border-[#2a2f38] bg-[#15191f] p-5 shadow-xl shadow-black/25">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.22em] text-amber-300">Beta</p>
          <p className="mt-1 text-lg font-black text-white">Korekta głosowa beta</p>
          <p className="text-xs text-slate-400">Opcjonalnie. Nic nie zmieniamy bez potwierdzenia.</p>
        </div>
        <button
          type="button"
          onClick={() => setIsExpanded((current) => !current)}
          className="flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-[#2a2f38] bg-[#0f1217] px-4 text-sm font-black text-slate-200 transition hover:border-amber-400/40 hover:text-amber-100"
        >
          {isExpanded ? 'Zwiń' : 'Pokaż'}
        </button>
      </div>
      {!isExpanded && (
        <p className="mt-3 rounded-xl border border-[#2a2f38] bg-[#0f1217] px-3 py-2 text-xs font-semibold text-slate-400">
          Główne liczenie działa z pól formularza. Głos zostaje jako pomocnicza funkcja testowa.
        </p>
      )}
      {isExpanded && (
        <>
          <button
            type="button"
            onClick={onStart}
            disabled={isListening}
            className="mt-3 flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-amber-500 px-4 text-sm font-black text-[#0b0d10] transition hover:bg-amber-400 disabled:opacity-50"
          >
            {isListening ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mic className="h-4 w-4" />}
            Powiedz zmianę
          </button>
          <textarea
            value={transcript}
            onChange={(event) => onTranscriptChange(event.target.value)}
            rows={2}
            placeholder="np. Zmień metraż na 83 metry"
            className={`${FIELD_CONTROL_CLASS} mt-3 min-h-20 resize-none py-3`}
          />
          {parsedCommand && (
            <div className={`mt-3 rounded-lg border px-3 py-2 text-sm ${canApply ? 'border-[#00e676]/30 bg-[#00e676]/10 text-[#00e676]' : 'border-amber-400/30 bg-amber-500/10 text-amber-200'}`}>
              {formatVoiceCommandPreview(parsedCommand)}
            </div>
          )}
          {errorMessage && <div className="mt-3 rounded-lg border border-amber-400/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">{errorMessage}</div>}
          {statusMessage && <div className="mt-3 rounded-lg border border-cyan-400/30 bg-cyan-500/10 px-3 py-2 text-sm text-cyan-100">{statusMessage}</div>}
          <button
            type="button"
            onClick={onApply}
            disabled={!canApply}
            className="mt-3 min-h-12 w-full rounded-2xl bg-amber-500 px-4 text-sm font-black text-[#0b0d10] transition hover:bg-amber-400 disabled:bg-slate-700 disabled:text-slate-400"
          >
            Zastosuj zmianę
          </button>
        </>
      )}
    </section>
  );
});

const SimpleEstimateCard = memo(function SimpleEstimateCard({
  form,
  region,
  isManualMode,
  isCalculating,
  errorMessage,
  canCalculate,
  onChange,
  onCalculate,
}: {
  form: QuickFormState;
  region: string;
  isManualMode: boolean;
  isCalculating: boolean;
  errorMessage: string | null;
  canCalculate: boolean;
  onChange: (patch: Partial<QuickFormState>) => void;
  onCalculate: () => void;
}) {
  const roomLengthM = parsePositiveNumber(form.roomLength);
  const roomWidthM = parsePositiveNumber(form.roomWidth);
  const surfaceCalculation = React.useMemo(
    () => calculateSurfaceForQuickForm(form),
    [form],
  );
  const rateSuggestion = getScopeRateSuggestion(form.manualScope, region);
  const workingRateItems = getWorkingRateItemsForQuickScope(form.manualScope);
  const visibleWorkingRateItems = workingRateItems.slice(0, 6);
  const hiddenWorkingRateItemsCount = Math.max(0, workingRateItems.length - visibleWorkingRateItems.length);
  const suggestedRate = rateSuggestion.rate;
  const customRate = parsePositiveNumber(form.customLaborRate);
  const selectedRate = form.laborRateMode === 'custom' && customRate ? customRate : suggestedRate;
  const rateLabel = getPrimaryRateLabel();
  const isBathroom = form.manualScope === 'bathroom';
  const isPaving = isPavingScope(form.manualScope);
  const pavingAreaM2 = parsePositiveNumber(form.pavingArea);
  const pavingLengthM = parsePositiveNumber(form.pavingLength);
  const pavingWidthM = parsePositiveNumber(form.pavingWidth);
  const pavingCurbM = parsePositiveNumber(form.curbLength);
  const pavingWorkingAreaM2 = computePavingWorkingAreaM2(pavingAreaM2, pavingLengthM, pavingWidthM);
  const edgesOnly = isPaving && (form.pavingMode === 'edges_only' ||
    (form.pavingMode === undefined && pavingWorkingAreaM2 === 0 && Boolean(pavingCurbM)));

  return (
    <section className="rounded-[28px] border border-[#2a2f38] bg-[#15191f] p-5 shadow-xl shadow-black/25">
      <div className="grid gap-3">
        <CoreStatusBadgeRow>
          <CoreStatusBadge label="Zdjęcie: kontekst" tone="info" />
          {isManualMode && <CoreStatusBadge label="Liczenie lokalne" tone="amber" />}
        </CoreStatusBadgeRow>
        <div>
          <p className="text-xs font-black uppercase tracking-[0.22em] text-amber-300">Wycena</p>
          <h2 className="mt-2 text-2xl font-black text-white">Dane do wyceny</h2>
          <p className="mt-1 text-sm text-slate-400">Zdjęcie pomaga ocenić zakres i stan prac. Metraż liczysz i zatwierdzasz Ty.</p>
        </div>
        <div className="rounded-2xl border border-[#2a2f38] bg-[#0f1217] p-4">
          {isPaving ? (
            <div className="grid gap-3 sm:grid-cols-3">
              <FieldLabel label="Zakres bruku">
                <select value={edgesOnly ? 'edges_only' : 'surface'} onChange={(event) => onChange({
                  pavingMode: event.target.value as 'surface' | 'edges_only',
                  laborRateMode: event.target.value === 'edges_only' ? 'custom' : 'suggested',
                  customLaborRate: '',
                  ...(event.target.value === 'edges_only' ? { materialMode: 'client', materialClientPrice: '' } : {}),
                })} className={`${FIELD_CONTROL_CLASS} min-h-14`}>
                  <option value="surface">Kostka w m²</option>
                  <option value="edges_only">Tylko obrzeża / krawężniki</option>
                </select>
              </FieldLabel>
              {!edgesOnly && <>
              <FieldLabel label="Powierzchnia do ułożenia (m²)">
                <input type="number" min="0" step="0.1" value={form.pavingArea} onChange={(event) => onChange({ pavingArea: event.target.value })} inputMode="decimal" placeholder="np. 40" className={FIELD_CONTROL_CLASS} />
              </FieldLabel>
              <FieldLabel label="Długość terenu (m)">
                <input type="number" min="0" step="0.01" value={form.pavingLength} onChange={(event) => onChange({ pavingLength: event.target.value })} inputMode="decimal" placeholder="np. 8" className={FIELD_CONTROL_CLASS} />
              </FieldLabel>
              <FieldLabel label="Szerokość terenu (m)">
                <input type="number" min="0" step="0.01" value={form.pavingWidth} onChange={(event) => onChange({ pavingWidth: event.target.value })} inputMode="decimal" placeholder="np. 5" className={FIELD_CONTROL_CLASS} />
              </FieldLabel>
              </>}
              <FieldLabel label="Obrzeża / krawężniki (mb)">
                <input type="number" min="0" step="0.1" value={form.curbLength} onChange={(event) => onChange({ curbLength: event.target.value })} inputMode="decimal" placeholder="np. 18" className={FIELD_CONTROL_CLASS} />
              </FieldLabel>
              {!edgesOnly && <>
              <FieldLabel label="Grubość kostki">
                <select value={form.pavingThickness} onChange={(event) => onChange({ pavingThickness: event.target.value as QuickPavingThickness })} className={FIELD_CONTROL_CLASS}>
                  <option value="6cm">6 cm</option>
                  <option value="8cm">8 cm</option>
                  <option value="confirm">do ustalenia</option>
                </select>
              </FieldLabel>
              <FieldLabel label="Podbudowa">
                <select value={form.pavingSubbase} onChange={(event) => onChange({ pavingSubbase: event.target.value as QuickPavingSubbase })} className={FIELD_CONTROL_CLASS}>
                  <option value="ready">gotowa</option>
                  <option value="todo">do wykonania</option>
                  <option value="confirm">do ustalenia</option>
                </select>
              </FieldLabel>
              </>}
              <FieldLabel label="Transport">
                <select value={form.pavingTransport} onChange={(event) => onChange({ pavingTransport: event.target.value as QuickPavingTransport })} className={FIELD_CONTROL_CLASS}>
                  <option value="client">klient</option>
                  <option value="contractor">wykonawca</option>
                  <option value="confirm">do ustalenia</option>
                </select>
              </FieldLabel>
              <p className="rounded-xl border border-cyan-400/20 bg-cyan-500/10 px-3 py-2 text-sm text-cyan-100 sm:col-span-3">
                {edgesOnly ? 'Podaj długość i własną stawkę za mb. Cena obejmie samą robociznę montażu obrzeży / krawężników.' : 'Podaj powierzchnię albo długość i szerokość terenu. Sam montaż obrzeży wybierz w polu Zakres bruku.'}
              </p>
              {!edgesOnly && <>
              <p className="text-xs text-slate-400 sm:col-span-3">
                Grubość kostki to informacja do oferty i doboru materiału. Nie zmienia automatycznie stawki robocizny.
              </p>
              <p className="text-xs text-slate-500 sm:col-span-3">
                Materiały (kostka + zapas, obrzeża, geowłóknina, kruszywo, podsypka) traktuj orientacyjnie i potwierdź z hurtownią.
              </p></>}
            </div>
          ) : (
            <>
          <p className="mb-2 text-sm font-black text-white">{QUICK_SURFACE_MODE_QUESTION}</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {QUICK_SURFACE_MODE_OPTIONS.map(({ mode, label, description }) => (
              <button
                key={mode}
                type="button"
                aria-pressed={form.surfaceMode === mode}
                onClick={() => onChange({ surfaceMode: mode })}
                className={`min-h-12 rounded-xl border px-3 py-3 text-left text-sm font-black transition ${
                  form.surfaceMode === mode
                    ? 'border-amber-400 bg-amber-500/15 text-amber-100'
                    : 'border-[#2a2f38] bg-[#15191f] text-slate-300 hover:border-amber-400/40'
                }`}
              >
                {label}
                <span className="mt-1 block text-xs font-semibold text-slate-400">{description}</span>
              </button>
            ))}
          </div>

          {form.surfaceMode === 'ready' ? (
            <div className="mt-4">
              <FieldLabel label={QUICK_READY_AREA_FIELD_LABEL}>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.readyWorkingArea}
                  onChange={(event) => onChange({ readyWorkingArea: event.target.value })}
                  inputMode="decimal"
                  placeholder="np. 192,3"
                  className={FIELD_CONTROL_CLASS}
                />
                <p className="mt-2 text-xs text-slate-400">
                  Wpisz finalny metraż po własnym obmiarze. ProWycena nie doliczy ukrytego sufitu ani otworów.
                </p>
              </FieldLabel>
            </div>
          ) : form.surfaceMode === 'room' || form.surfaceMode === 'wall' ? (
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <FieldLabel label={getQuickLengthFieldLabel(form.surfaceMode)}>
                <input type="number" min="0" step="0.01" value={form.roomLength} onChange={(event) => onChange({ roomLength: event.target.value })} inputMode="decimal" placeholder="np. 5" className={FIELD_CONTROL_CLASS} />
              </FieldLabel>
              {form.surfaceMode === 'room' && (
                <FieldLabel label="Szerokość pomieszczenia (m)">
                  <input type="number" min="0" step="0.01" value={form.roomWidth} onChange={(event) => onChange({ roomWidth: event.target.value })} inputMode="decimal" placeholder="np. 5" className={FIELD_CONTROL_CLASS} />
                </FieldLabel>
              )}
              <FieldLabel label="Wysokość (m)">
                <input type="number" min="0" step="0.01" value={form.manualHeight} onChange={(event) => onChange({ manualHeight: event.target.value })} inputMode="decimal" placeholder="np. 2.65" className={FIELD_CONTROL_CLASS} />
              </FieldLabel>
              <FieldLabel label="Drzwi (szt.)">
                <input type="number" min="0" step="1" value={form.doorCount} onChange={(event) => onChange({ doorCount: event.target.value })} inputMode="numeric" placeholder="np. 1" className={FIELD_CONTROL_CLASS} />
                <p className="mt-2 text-xs text-slate-400">2 m² / szt. odliczane automatycznie.</p>
              </FieldLabel>
              <FieldLabel label="Nietypowe otwory do odliczenia (m²)">
                <input type="number" min="0" step="0.1" value={form.largeOpeningsArea} onChange={(event) => onChange({ largeOpeningsArea: event.target.value })} inputMode="decimal" placeholder="np. 0" className={FIELD_CONTROL_CLASS} />
                <p className="mt-2 text-xs text-slate-400">
                  Wpisz łączną powierzchnię dużych otworów, np. bramy garażowe, duże drzwi, witryny, przejścia lub okna powyżej 3 m².
                  Standardowe drzwi są odliczane automatycznie jako 2 m²/szt. Małych okien nie wpisujemy w szybkim obmiarze.
                </p>
              </FieldLabel>
              {form.surfaceMode === 'room' && (
                <label className="flex min-h-12 cursor-pointer items-center gap-3 rounded-lg border border-[#2a2f38] bg-[#15191f] px-3 py-3 text-sm font-bold text-white">
                  <input
                    type="checkbox"
                    checked={form.includeCeiling}
                    onChange={(event) => onChange({ includeCeiling: event.target.checked })}
                    className="h-5 w-5 accent-amber-500"
                  />
                  {QUICK_ROOM_CEILING_LABEL}
                </label>
              )}
            </div>
          ) : (
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <FieldLabel label="Powierzchnia mieszkania (m²)">
                <input type="number" min="0" step="0.1" value={form.apartmentArea} onChange={(event) => onChange({ apartmentArea: event.target.value })} inputMode="decimal" placeholder="np. 49" className={FIELD_CONTROL_CLASS} />
              </FieldLabel>
              <FieldLabel label="Wysokość (m)">
                <input type="number" min="0" step="0.01" value={form.apartmentHeight} onChange={(event) => onChange({ apartmentHeight: event.target.value })} inputMode="decimal" placeholder="2.65" className={FIELD_CONTROL_CLASS} />
              </FieldLabel>
              <FieldLabel label="Liczba drzwi (szt.)">
                <input type="number" min="0" step="1" value={form.apartmentDoorCount} onChange={(event) => onChange({ apartmentDoorCount: event.target.value })} inputMode="numeric" placeholder="np. 4" className={FIELD_CONTROL_CLASS} />
                <p className="mt-2 text-xs text-slate-400">2 m² / szt. odliczane automatycznie.</p>
              </FieldLabel>
              <FieldLabel label="Nietypowe otwory do odliczenia (m²)">
                <input type="number" min="0" step="0.1" value={form.apartmentLargeOpeningsArea} onChange={(event) => onChange({ apartmentLargeOpeningsArea: event.target.value })} inputMode="decimal" placeholder="np. 0" className={FIELD_CONTROL_CLASS} />
              </FieldLabel>
              <FieldLabel label="Łazienka / pomieszczenia mokre (m²)">
                <input type="number" min="0" step="0.1" value={form.bathroomArea} onChange={(event) => onChange({ bathroomArea: event.target.value })} inputMode="decimal" placeholder="np. 5" className={FIELD_CONTROL_CLASS} />
              </FieldLabel>
              <FieldLabel label="Układ mieszkania">
                <select value={form.apartmentLayout} onChange={(event) => onChange({ apartmentLayout: event.target.value as ApartmentLayoutMode })} className={FIELD_CONTROL_CLASS}>
                  <option value="open">Open space / mało ścian</option>
                  <option value="standard">Standard</option>
                  <option value="many_rooms">Dużo pokoi / korytarzy</option>
                </select>
              </FieldLabel>
              <label className="flex min-h-12 cursor-pointer items-center gap-3 rounded-lg border border-[#2a2f38] bg-[#15191f] px-3 py-3 text-sm font-bold text-white sm:col-span-3">
                <input
                  type="checkbox"
                  checked={form.includeCeiling}
                  onChange={(event) => onChange({ includeCeiling: event.target.checked })}
                  className="h-5 w-5 accent-amber-500"
                />
                {QUICK_APARTMENT_CEILING_LABEL}
              </label>
              {/* Offered only when there is a bathroom area for it to act on —
                  at 0 m² the surface calculation ignores it anyway. */}
              {shouldShowBathroomCeilingOnly(form.bathroomArea) && (
                <div className="rounded-lg border border-[#2a2f38] bg-[#15191f] px-3 py-3 sm:col-span-3">
                  <label className="flex min-h-12 cursor-pointer items-center gap-3 text-sm font-bold text-white">
                    <input
                      type="checkbox"
                      checked={form.bathroomCeilingOnly}
                      onChange={(event) => onChange({ bathroomCeilingOnly: event.target.checked })}
                      className="h-5 w-5 accent-amber-500"
                    />
                    {QUICK_BATHROOM_CEILING_ONLY_LABEL}
                  </label>
                  <p className="mt-2 text-xs text-slate-400">{QUICK_BATHROOM_CEILING_ONLY_HINT}</p>
                </div>
              )}
              <p className="rounded-xl border border-cyan-400/20 bg-cyan-500/10 px-3 py-2 text-sm text-cyan-100 sm:col-span-3">
                {QUICK_APARTMENT_ESTIMATE_NOTE}
              </p>
            </div>
          )}
            </>
          )}
        </div>
        {isPaving && !edgesOnly && pavingWorkingAreaM2 > 0 && (
          <div className="grid gap-2 rounded-xl border border-[#2a2f38] bg-[#0f1217] p-3 text-sm text-slate-300 sm:grid-cols-2">
            <EstimateFieldRow label="Powierzchnia do ułożenia" value={formatSurfaceM2(pavingWorkingAreaM2)} source="manual" />
            {pavingCurbM && pavingCurbM > 0 ? (
              <EstimateFieldRow label="Obrzeża / krawężniki" value={`${pavingCurbM} mb`} source="manual" />
            ) : null}
            <EstimateFieldRow label="Grubość kostki" value={PAVING_THICKNESS_LABELS[form.pavingThickness]} source="manual" />
            <EstimateFieldRow label="Podbudowa" value={PAVING_SUBBASE_LABELS[form.pavingSubbase]} source="manual" />
            <EstimateFieldRow label="Transport" value={PAVING_TRANSPORT_LABELS[form.pavingTransport]} source="manual" />
            <EstimateFieldRow label="Status" value="do potwierdzenia przez wykonawcę" source="confirm" />
          </div>
        )}
        {isBathroom && surfaceCalculation.floorAreaM2 > 0 && (
          <div className="grid gap-2 rounded-xl border border-[#2a2f38] bg-[#0f1217] p-3 text-sm text-slate-300 sm:grid-cols-2">
            <EstimateFieldRow label="Powierzchnia płytek do wyceny" value={formatSurfaceM2(surfaceCalculation.floorAreaM2)} source="manual" />
            <EstimateFieldRow label="Status" value="do potwierdzenia przez wykonawcę" source="confirm" />
            <p className="rounded-lg border border-cyan-400/20 bg-cyan-500/10 px-3 py-2 text-sm text-cyan-100 sm:col-span-2">
              {surfaceCalculation.mode === 'room' && roomLengthM && roomWidthM
                ? `Wyliczono z wymiarów podanych przez wykonawcę: ${round1(roomLengthM)} × ${round1(roomWidthM)} m`
                : 'Powierzchnia przyjęta z danych wpisanych przez wykonawcę.'}
            </p>
          </div>
        )}
        {!isPaving && !isBathroom && surfaceCalculation.finalWorkingAreaM2 > 0 && (
          <div className="grid gap-2 rounded-xl border border-[#2a2f38] bg-[#0f1217] p-3 text-sm text-slate-300 sm:grid-cols-2">
            {surfaceCalculation.mode === 'ready' ? (
              <>
                <EstimateFieldRow label="Gotowa powierzchnia robocza" value={formatSurfaceM2(surfaceCalculation.finalWorkingAreaM2)} source={getQuickGeometrySource('readyWorkingArea')} />
                <EstimateFieldRow label="Status" value={surfaceCalculation.confirmationStatus ?? 'zatwierdzona przez wykonawcę'} source="manual" />
              </>
            ) : surfaceCalculation.mode === 'apartment' ? (
              <>
                <EstimateFieldRow label="Powierzchnia mieszkania" value={formatSurfaceM2(surfaceCalculation.apartmentAreaM2 ?? surfaceCalculation.floorAreaM2)} source={getQuickGeometrySource('apartmentArea')} />
                <EstimateFieldRow label="Szacowana powierzchnia do wyceny" value={formatSurfaceM2(surfaceCalculation.finalWorkingAreaM2)} source={getQuickGeometrySource('finalWorkingArea')} />
                {/* An explicit 0 m² deduction stays visible — zero is a value, not missing data. */}
                <EstimateFieldRow label="Drzwi odliczone" value={formatQuickDeductionM2(surfaceCalculation.doorDeductionM2)} source={getQuickGeometrySource('doorDeduction')} />
                <EstimateFieldRow label="Nietypowe otwory" value={formatQuickDeductionM2(surfaceCalculation.largeOpeningsDeductionM2)} source={getQuickGeometrySource('largeOpeningsDeduction')} />
                <EstimateFieldRow label="Łazienka" value={surfaceCalculation.bathroomModeLabel ?? 'brak'} source={getQuickGeometrySource('bathroomMode')} />
                <EstimateFieldRow label="Status" value={surfaceCalculation.confirmationStatus ?? 'do potwierdzenia'} source="confirm" />
              </>
            ) : (
              <>
                {typeof surfaceCalculation.perimeterM === 'number' && surfaceCalculation.perimeterM > 0 && (
                  <EstimateFieldRow label="Obwód" value={`${formatSurfaceValue(surfaceCalculation.perimeterM)} m`} source={getQuickGeometrySource('perimeter')} />
                )}
                {surfaceCalculation.mode === 'room' && (
                  <EstimateFieldRow label="Powierzchnia podłogi / sufitu" value={formatSurfaceM2(surfaceCalculation.floorAreaM2)} source={getQuickGeometrySource('floorArea', surfaceCalculation.mode)} />
                )}
                <EstimateFieldRow label="Ściany brutto" value={formatSurfaceM2(surfaceCalculation.wallGrossAreaM2)} source={getQuickGeometrySource('wallGross')} />
                {/* An explicit 0 m² deduction stays visible — zero is a value, not missing data. */}
                <EstimateFieldRow label="Drzwi odliczone" value={formatQuickDeductionM2(surfaceCalculation.doorDeductionM2)} source={getQuickGeometrySource('doorDeduction')} />
                <EstimateFieldRow label="Duże otwory odliczone" value={formatQuickDeductionM2(surfaceCalculation.largeOpeningsDeductionM2)} source={getQuickGeometrySource('largeOpeningsDeduction')} />
                <EstimateFieldRow label="Ściany po odliczeniach" value={formatSurfaceM2(surfaceCalculation.wallNetAreaM2)} source={getQuickGeometrySource('wallNet')} />
                {surfaceCalculation.mode === 'room' && (
                  <EstimateFieldRow label="Sufit" value={formatSurfaceM2(surfaceCalculation.ceilingAreaM2)} source={getQuickGeometrySource('ceilingArea')} />
                )}
                <EstimateFieldRow label="Razem do wyceny" value={formatSurfaceM2(surfaceCalculation.finalWorkingAreaM2)} source={getQuickGeometrySource('finalWorkingArea')} />
              </>
            )}
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <FieldLabel label="Szybki start — zakres do wyceny">
            <select value={form.manualScope} onChange={(event) => onChange({ manualScope: event.target.value as QuickScope })} className={FIELD_CONTROL_CLASS}>
              {scopeOptions.map(([id, label]) => (
                <option key={id} value={id}>{label}</option>
              ))}
            </select>
          </FieldLabel>
        </div>
        {form.manualScope === 'other' && (
          <FieldLabel label="Własny zakres do wyceny">
            <textarea
              value={form.customScope}
              onChange={(event) => onChange({ customScope: event.target.value })}
              rows={3}
              placeholder="np. gruntowanie, gładź i malowanie sufitu oraz ścian"
              className={`${FIELD_CONTROL_CLASS} resize-none py-3`}
            />
          </FieldLabel>
        )}
        <div className="rounded-2xl border border-[#2a2f38] bg-[#0f1217] p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-white">{edgesOnly ? 'Same obrzeża — stawka zł/mb' : rateLabel}</p>
              {edgesOnly ? <p className="text-sm text-slate-300">Wpisz własną stawkę za mb. Wycena obejmie tylko podaną długość obrzeży.</p> : <>
              <p className="text-xs text-slate-400">
                Widełki regionalne: {rateSuggestion.rateMin}–{rateSuggestion.rateMax} {rateSuggestion.unit}
                {rateSuggestion.regionLabel ? ` (${rateSuggestion.regionLabel})` : ''}
              </p>
              <p className="mt-1 text-xs text-slate-400">
                {form.laborRateMode === 'custom' ? 'Własna stawka' : 'Użyta stawka'}: {selectedRate} {rateSuggestion.unit}
              </p>
              <p className="mt-1 text-xs text-slate-400">
                Zakres: {rateSuggestion.scopeDescription}.
              </p>
              <p className="mt-1 text-xs text-slate-500">
                Informacyjnie: {formatScopeRateBreakdown(rateSuggestion.components)}.
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {rateSuggestion.note}
              </p></>}
            </div>
            {!edgesOnly && <button
              type="button"
              onClick={() =>
                onChange({
                  laborRateMode: form.laborRateMode === 'custom' ? 'suggested' : 'custom',
                })
              }
              className="min-h-11 rounded-2xl border border-amber-400/40 bg-amber-500/10 px-3 text-sm font-bold text-amber-200 transition hover:bg-amber-500/15"
            >
              {form.laborRateMode === 'custom' ? 'Użyj sugerowanej' : 'Własna stawka'}
            </button>}
          </div>
          {form.laborRateMode === 'custom' && (
            <input
              aria-label={edgesOnly ? 'Własna stawka (zł/mb)' : 'Własna stawka (zł/m²)'}
              value={form.customLaborRate}
              onChange={(event) => onChange({ customLaborRate: event.target.value })}
              inputMode="decimal"
              placeholder={edgesOnly ? 'np. 35' : `np. ${suggestedRate}`}
              className={`${FIELD_CONTROL_CLASS} mt-3`}
            />
          )}
          <p className="mt-3 text-xs text-slate-400">
            {edgesOnly ? 'Własna stawka netto za montaż jednego metra bieżącego.' : 'To stawka orientacyjna za cały wybrany zakres. Możesz ją nadpisać ręcznie.'}
          </p>
        </div>
        <div className="rounded-2xl border border-[#2a2f38] bg-[#0f1217] p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-white">Stawki orientacyjne i pozycje do sprawdzenia</p>
              <p className="mt-1 text-xs text-slate-400">
                Roboczy cennik wykonawcy dla Olsztyna / Warmii. Pozycje są informacyjne i nie zmieniają ceny ani PDF bez Twojej decyzji.
              </p>
            </div>
            <span className="rounded-full border border-amber-400/30 bg-amber-500/10 px-3 py-1 text-xs font-black uppercase tracking-[0.16em] text-amber-200">
              do potwierdzenia
            </span>
          </div>
          <div className="mt-3 grid gap-2">
            {visibleWorkingRateItems.map((item) => (
              <div key={item.id} className="rounded-xl border border-[#2a2f38] bg-[#15191f] p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-black text-white">{item.label}</p>
                    <p className="mt-1 text-xs text-slate-500">{WORKING_RATE_CATEGORY_LABELS[item.category]}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-black text-amber-100">{formatWorkingRateValue(item)}</p>
                    <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">{WORKING_RATE_STATUS_LABELS[item.status]}</p>
                  </div>
                </div>
                <p className="mt-2 text-xs text-slate-400">{item.description}</p>
              </div>
            ))}
          </div>
          {hiddenWorkingRateItemsCount > 0 && (
            <p className="mt-2 text-xs font-semibold text-slate-500">
              + {hiddenWorkingRateItemsCount} pozycji w roboczym cenniku dla tego zakresu. Pełny edytor stawek zostaje w backlogu.
            </p>
          )}
          <p className="mt-3 rounded-xl border border-cyan-400/20 bg-cyan-500/10 px-3 py-2 text-xs font-semibold text-cyan-100">
            ProWycena tworzy roboczą wycenę do potwierdzenia przez wykonawcę. Nie zastępuje oględzin, kosztorysu normowego ani indywidualnych ustaleń.
          </p>
          {form.manualScope === 'bathroom' && (
            <p className="mt-2 rounded-xl border border-amber-400/20 bg-amber-500/10 px-3 py-2 text-xs font-semibold text-amber-100">
              Płytki liczymy od m² powierzchni płytek. Pełna łazienka to osobna orientacyjna wycena zależna od projektu, trudności i zakresu.
            </p>
          )}
        </div>
        <div className="rounded-2xl border border-[#2a2f38] bg-[#0f1217] p-4">
          <p className="text-sm font-semibold text-white">Materiały, VAT i dane do PDF</p>
          <p className="mt-1 text-xs text-slate-400">
            Ustaw, co ma trafić do oferty klienta. Dane zakupowe zostają widoczne tylko dla Ciebie.
          </p>
          <div className="mt-3 grid gap-2 sm:grid-cols-3">
            {([
              ['client', 'Materiał po stronie klienta'],
              ['contractor', 'Robocizna + materiał'],
              ['info', 'Informacyjnie / mieszane'],
            ] as Array<[QuickMaterialMode, string]>).map(([mode, label]) => (
              <button
                key={mode}
                type="button"
                onClick={() => onChange({ materialMode: mode })}
                className={`min-h-12 rounded-xl border px-3 text-sm font-bold transition ${
                  form.materialMode === mode
                    ? 'border-amber-400 bg-amber-500/15 text-amber-100'
                    : 'border-[#2a2f38] bg-[#15191f] text-slate-300 hover:border-amber-400/40'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {form.materialMode === 'contractor' && (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <FieldLabel label="Cena materiałów dla klienta">
                <input
                  value={form.materialClientPrice}
                  onChange={(event) => onChange({ materialClientPrice: event.target.value })}
                  inputMode="decimal"
                  placeholder="np. 1000"
                  className={FIELD_CONTROL_CLASS}
                />
              </FieldLabel>
              <FieldLabel label="Rabat (%)">
                <input
                  value={form.materialDiscountPercent}
                  onChange={(event) => onChange({ materialDiscountPercent: event.target.value })}
                  inputMode="decimal"
                  placeholder="np. 20"
                  className={FIELD_CONTROL_CLASS}
                />
              </FieldLabel>
              <p className="mt-2 text-xs text-slate-400">
                Rabat i zysk na materiale są widoczne tylko dla Ciebie. Nie trafiają do PDF klienta.
              </p>
            </div>
          )}
          <div className="mt-4">
            <p className="text-xs font-black uppercase tracking-[0.16em] text-slate-500">VAT</p>
            <div className="mt-2 grid gap-2 sm:grid-cols-4">
              {([
                ['no_vat', 'Bez VAT / zwolniony'],
                ['vat_8', 'VAT 8%'],
                ['vat_23', 'VAT 23%'],
                ['confirm', 'Do potwierdzenia'],
              ] as Array<[QuickVatMode, string]>).map(([mode, label]) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => onChange({ vatMode: mode })}
                  className={`min-h-12 rounded-xl border px-3 text-sm font-bold transition ${
                    form.vatMode === mode
                      ? 'border-[#00e676] bg-[#00e676]/10 text-[#00e676]'
                      : 'border-[#2a2f38] bg-[#15191f] text-slate-300 hover:border-[#00e676]/40'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <p className="mt-2 text-xs text-slate-400">
              Jeśli nie jesteś VAT-owcem, wybierz Bez VAT / zwolniony. Podatek dochodowy nie jest stawką VAT.
            </p>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <FieldLabel label="Nazwa wykonawcy / firmy">
              <input
                value={form.contractorName}
                onChange={(event) => onChange({ contractorName: event.target.value })}
                placeholder="np. Jan Kowalski Usługi Remontowe"
                className={FIELD_CONTROL_CLASS}
              />
            </FieldLabel>
            <FieldLabel label="NIP (opcjonalnie)">
              <input
                value={form.contractorNip}
                onChange={(event) => onChange({ contractorNip: event.target.value })}
                inputMode="numeric"
                placeholder="10 cyfr, jeśli chcesz go na ofercie"
                className={FIELD_CONTROL_CLASS}
              />
            </FieldLabel>
            <FieldLabel label="Telefon lub email">
              <input
                value={form.contractorContact}
                onChange={(event) => onChange({ contractorContact: event.target.value })}
                placeholder="telefon albo e-mail"
                className={FIELD_CONTROL_CLASS}
              />
            </FieldLabel>
          </div>
        </div>
        {errorMessage && <div className="rounded-lg border border-amber-400/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">{errorMessage}</div>}
        <button
          type="button"
          disabled={isCalculating || !canCalculate}
          onClick={onCalculate}
          className="mt-1 flex min-h-14 w-full items-center justify-center gap-2 rounded-2xl bg-[#00e676] px-4 text-base font-black text-black shadow-lg shadow-[#00e676]/15 transition hover:bg-emerald-300 disabled:bg-slate-700 disabled:text-slate-400 disabled:shadow-none"
        >
          {isCalculating ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
          {isCalculating ? 'Liczymy wycenę...' : 'Policz wycenę'}
        </button>
        {isCalculating && (
          <p className="rounded-lg border border-[#00e676]/25 bg-[#00e676]/10 px-3 py-2 text-sm font-semibold text-[#00e676]">
            Przeliczam dane ręczne i przygotowuję wynik. Zdjęcie nie blokuje wyceny.
          </p>
        )}
      </div>
    </section>
  );
});

const ResultCard = memo(function ResultCard({
  result,
  publicMode,
  userLabel,
  saveStatusMessage,
  pdfStatusMessage,
  pdfNoticeMessage,
  hasGeneratedPdf,
  isPdfDeliveryPending,
  pwaInstall,
  manualItems,
  marginShieldDecisions,
  onEdit,
  onLogin,
  onPdf,
  onSaveProject,
  isSavingProject,
  onProjects,
  onAddManualItem,
  onUpdateManualItem,
  onRemoveManualItem,
  onMarginShieldDecisionChange,
}: {
  result: QuickEstimateSummary;
  publicMode: boolean;
  userLabel?: string;
  saveStatusMessage?: string | null;
  pdfStatusMessage?: string | null;
  pdfNoticeMessage?: string | null;
  hasGeneratedPdf: boolean;
  isPdfDeliveryPending: boolean;
  pwaInstall: UsePwaInstallResult;
  manualItems: ManualEstimateItem[];
  marginShieldDecisions: Record<string, MarginShieldDecisionStatus>;
  onEdit: () => void;
  onLogin: () => void;
  onPdf: () => void | Promise<void>;
  onSaveProject: () => void | Promise<void>;
  isSavingProject: boolean;
  onProjects: () => void;
  onAddManualItem: (input: ManualItemInput) => void;
  onUpdateManualItem: (id: string, input: ManualItemInput) => void;
  onRemoveManualItem: (id: string) => void;
  onMarginShieldDecisionChange: (alertId: string, status: MarginShieldDecisionStatus) => void;
}) {
  const risks = (result.risks.length > 0 ? result.risks : ['brak dodatkowych zaleceń w szybkim obmiarze'])
    .map(translateVisionText);
  const hasLowConfidence =
    typeof result.confidence === 'number' &&
    result.confidence > 0 &&
    result.confidence < MIN_CONFIDENCE_TO_SHOW;
  const confidenceText = hasLowConfidence
    ? 'Za mało danych na zdjęciu. Wpisz metraż ręcznie albo dodaj więcej zdjęć.'
    : result.hasRealConfidence && typeof result.confidence === 'number'
    ? `Pewność rozpoznania kontekstu: ${result.confidence}% - metraż zatwierdza wykonawca.`
    : 'Metraż i zakres zatwierdza wykonawca.';
  const isBathroom = result.scope === 'bathroom';
  const bathroomTileAreaM2 = result.engineInput.tilesAreaM2;
  const bathroomMeasurementSource =
    result.engineInput.projectMode === 'room' && result.engineInput.length && result.engineInput.width
      ? `Wyliczono z wymiarów podanych przez wykonawcę: ${round1(result.engineInput.length)} × ${round1(result.engineInput.width)} m`
      : 'Powierzchnia przyjęta z danych wpisanych przez wykonawcę.';
  const floorAreaValue = formatKnownArea(result.floorAreaM2, result.floorAreaSource);
  const workingAreaValue =
    result.workingAreaM2 && result.workingAreaM2 > 0
      ? `ok. ${formatSurfaceM2(result.workingAreaM2)}`
      : 'do potwierdzenia przez wykonawcę';
  const aiObservations = result.aiObservations;
  const materialCatalogItems = getMaterialCatalogForScope(result.scope);
  const marginShieldAudit = buildMarginShieldAuditForResult(result);
  const approvedOffer = buildQuickApprovedOffer(result, marginShieldDecisions, manualItems);
  const marginShieldAddedNet = sumMoney(
    approvedOffer.marginShieldAddedItems.map((item) => item.totalNetto)
  );
  const manualItemsNet = sumMoney(manualItems.map((item) => item.totalNetto));
  const displayPriceFrom = sumMoney([result.priceFrom, marginShieldAddedNet, manualItemsNet]);
  const displayPriceTo = sumMoney([result.priceTo, marginShieldAddedNet, manualItemsNet]);
  const materialPreview = calculateMaterialInternalPreview(
    result.estimate.meta.materialSuggestions,
    result.materialDiscountPercent,
    result.materialClientPrice
  );
  const offerPricing = approvedOffer.pricing;

  return (
    <section className="rounded-[28px] border border-amber-400/30 bg-[#15191f] p-5 shadow-xl shadow-black/25">
      <CoreStatusBadgeRow>
        <CoreStatusBadge
          label={result.hasVisionAnalysis ? 'Zdjęcie: kontekst' : 'Zdjęcie nieprzeanalizowane'}
          tone={result.hasVisionAnalysis ? 'info' : 'amber'}
        />
        <CoreStatusBadge label="Kalkulacja wykonawcy" tone="amber" />
        <CoreStatusBadge label={publicMode ? 'PDF testowy' : 'Gotowe do PDF'} tone="success" />
        {result.isLocalMode && <CoreStatusBadge label="Liczenie lokalne" tone="amber" />}
      </CoreStatusBadgeRow>
      {!result.hasVisionAnalysis && (
        <p className="mt-2 text-xs font-semibold text-amber-200">
          Zdjęcie nie zostało przeanalizowane. Wycena opiera się wyłącznie na danych wpisanych
          przez wykonawcę.
        </p>
      )}
      {result.hasVisionAnalysis && result.isLocalMode && (
        <p className="mt-2 text-xs font-semibold text-slate-400">
          Zdjęcie jest tylko kontekstem, nie pomiarem. Metraż zatwierdza wykonawca.
        </p>
      )}
      {userLabel && (
        <p className="mt-2 rounded-xl border border-[#00e676]/25 bg-[#00e676]/10 px-3 py-2 text-sm font-semibold text-[#00e676]">
          Zalogowany jako {userLabel}
        </p>
      )}
      <p className="mt-3 text-sm font-semibold text-amber-200">
        Orientacyjna wycena do potwierdzenia
      </p>
      <h2 className="mt-2 text-3xl font-black tracking-tight text-white sm:text-4xl">{formatPriceRange(displayPriceFrom, displayPriceTo)}</h2>
      {marginShieldAddedNet > 0 && (
        <p className="mt-2 text-sm font-semibold text-[#00e676]">
          Dodane z Tarczy Marży do ceny: {safeFormatPLN(marginShieldAddedNet)}
        </p>
      )}
      <div className="mt-4 rounded-2xl border border-[#2a2f38] bg-[#0f1217] p-4">
        <p className="text-sm font-semibold text-white">Dane wejściowe</p>
        <div className="mt-3 grid gap-2 text-sm text-slate-300 sm:grid-cols-2">
          {isBathroom ? (
            <>
              <EstimateFieldRow
                label="Powierzchnia płytek do wyceny"
                value={bathroomTileAreaM2 && bathroomTileAreaM2 > 0 ? formatSurfaceM2(bathroomTileAreaM2) : 'do potwierdzenia przez wykonawcę'}
                source="manual"
              />
              <EstimateFieldRow label="Zakres" value={result.scopeLabel} source={result.scopeSource} />
              <EstimateFieldRow label="Status" value="do potwierdzenia przez wykonawcę" source="confirm" />
              <p className="rounded-lg border border-cyan-400/20 bg-cyan-500/10 px-3 py-2 text-sm text-cyan-100 sm:col-span-2">
                {bathroomMeasurementSource}
              </p>
              <EstimateFieldRow label="Źródło" value={result.inputSourceLabel} source={getSourceBadgeTone(result.floorAreaSource)} />
            </>
          ) : (
            <>
              {result.surfaceCalculation?.mode !== 'ready' &&
                result.surfaceCalculation?.mode !== 'wall' && (
                  <EstimateFieldRow
                    label="Powierzchnia podłogi / sufitu"
                    value={floorAreaValue}
                    source={
                      result.surfaceCalculation
                        ? getQuickGeometrySource('floorArea', result.surfaceCalculation.mode)
                        : result.floorAreaSource
                    }
                  />
                )}
              <EstimateFieldRow label="Razem do wyceny" value={workingAreaValue} source={getQuickGeometrySource('finalWorkingArea')} />
              <EstimateFieldRow label="Zakres" value={result.scopeLabel} source={result.scopeSource} />
              {result.visionSummary.surfaceCondition && (
                <EstimateFieldRow
                  label="Stan podłoża"
                  value={result.visionSummary.surfaceCondition}
                  source={result.visionSummary.surfaceConditionSource ?? 'confirm'}
                />
              )}
              {typeof result.manualHeightM === 'number' && result.manualHeightM > 0 && (
                <EstimateFieldRow label="Wysokość" value={`${result.manualHeightM} m`} source="manual" />
              )}
              {result.surfaceCalculation?.mode === 'apartment' && (
                <>
                  <EstimateFieldRow label="Powierzchnia mieszkania" value={formatSurfaceM2(result.surfaceCalculation.apartmentAreaM2 ?? result.surfaceCalculation.floorAreaM2)} source={getQuickGeometrySource('apartmentArea')} />
                  <EstimateFieldRow label="Szacowana powierzchnia do wyceny" value={formatSurfaceM2(result.surfaceCalculation.finalWorkingAreaM2)} source={getQuickGeometrySource('finalWorkingArea')} />
                  <EstimateFieldRow label="Drzwi odliczone" value={formatQuickDeductionM2(result.surfaceCalculation.doorDeductionM2)} source={getQuickGeometrySource('doorDeduction')} />
                  <EstimateFieldRow label="Nietypowe otwory" value={formatQuickDeductionM2(result.surfaceCalculation.largeOpeningsDeductionM2)} source={getQuickGeometrySource('largeOpeningsDeduction')} />
                  <EstimateFieldRow label="Łazienka" value={result.surfaceCalculation.bathroomModeLabel ?? 'brak'} source={getQuickGeometrySource('bathroomMode')} />
                  <EstimateFieldRow label="Status" value={result.surfaceCalculation.confirmationStatus ?? 'do potwierdzenia'} source="confirm" />
                </>
              )}
              {result.surfaceCalculation?.mode === 'room' && (
                <>
                  {typeof result.surfaceCalculation.perimeterM === 'number' && result.surfaceCalculation.perimeterM > 0 && (
                    <EstimateFieldRow label="Obwód" value={`${formatSurfaceValue(result.surfaceCalculation.perimeterM)} m`} source={getQuickGeometrySource('perimeter')} />
                  )}
                  <EstimateFieldRow label="Ściany brutto" value={formatSurfaceM2(result.surfaceCalculation.wallGrossAreaM2)} source={getQuickGeometrySource('wallGross')} />
                  {/* An explicit 0 m² deduction stays visible — zero is a value, not missing data. */}
                  <EstimateFieldRow label="Drzwi / odliczenia" value={formatQuickDeductionM2(result.surfaceCalculation.doorDeductionM2)} source={getQuickGeometrySource('doorDeduction')} />
                  <EstimateFieldRow label="Duże otwory odliczone" value={formatQuickDeductionM2(result.surfaceCalculation.largeOpeningsDeductionM2)} source={getQuickGeometrySource('largeOpeningsDeduction')} />
                  <EstimateFieldRow label="Ściany po odliczeniach" value={formatSurfaceM2(result.surfaceCalculation.wallNetAreaM2)} source={getQuickGeometrySource('wallNet')} />
                  <EstimateFieldRow label="Sufit" value={formatSurfaceM2(result.surfaceCalculation.ceilingAreaM2)} source={getQuickGeometrySource('ceilingArea')} />
                  <EstimateFieldRow label="Razem do wyceny" value={formatSurfaceM2(result.surfaceCalculation.finalWorkingAreaM2)} source={getQuickGeometrySource('finalWorkingArea')} />
                </>
              )}
              {result.surfaceCalculation?.mode === 'ready' && (
                <EstimateFieldRow
                  label="Gotowa powierzchnia robocza"
                  value={formatSurfaceM2(result.surfaceCalculation.finalWorkingAreaM2)}
                  source={getQuickGeometrySource('readyWorkingArea')}
                />
              )}
              {result.surfaceCalculation?.mode === 'wall' && (
                <>
                  <EstimateFieldRow label="Ściana brutto" value={formatSurfaceM2(result.surfaceCalculation.wallGrossAreaM2)} source={getQuickGeometrySource('wallGross')} />
                  {/* An explicit 0 m² deduction stays visible — zero is a value, not missing data. */}
                  <EstimateFieldRow label="Drzwi odliczone" value={formatQuickDeductionM2(result.surfaceCalculation.doorDeductionM2)} source={getQuickGeometrySource('doorDeduction')} />
                  <EstimateFieldRow label="Nietypowe otwory odliczone" value={formatQuickDeductionM2(result.surfaceCalculation.largeOpeningsDeductionM2)} source={getQuickGeometrySource('largeOpeningsDeduction')} />
                  <EstimateFieldRow label="Ściana po odliczeniach" value={formatSurfaceM2(result.surfaceCalculation.finalWorkingAreaM2)} source={getQuickGeometrySource('wallNet')} />
                </>
              )}
              <EstimateFieldRow label="Źródło" value={result.inputSourceLabel} source={getSourceBadgeTone(result.floorAreaSource)} />
            </>
          )}
        </div>
        <p className="mt-3 text-sm text-slate-300">{confidenceText}</p>
        {result.visionSummary.chips.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {result.visionSummary.chips.map((chip) => (
              <span key={chip} className="rounded-full border border-[#00e676]/30 bg-[#00e676]/10 px-3 py-1 text-xs font-semibold text-[#00e676]">{chip}</span>
            ))}
          </div>
        )}
      </div>
      {aiObservations.length > 0 && (
        <div className="mt-3 rounded-2xl border border-[#2a2f38] bg-[#0f1217] p-4">
          <p className="text-sm font-semibold text-white">Kontekst ze zdjęcia</p>
          <ul className="mt-2 grid gap-2 text-sm text-slate-300">
            {aiObservations.map((item) => (
              <li key={item} className="rounded-lg border border-[#2a2f38] bg-[#15191f] px-3 py-2">{item}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="mt-3 rounded-2xl border border-[#2a2f38] bg-[#0f1217] p-4">
        <p className="text-sm font-semibold text-white">Do potwierdzenia przez wykonawcę</p>
        <ul className="mt-2 grid gap-2 text-sm text-slate-300 sm:grid-cols-2">
          {result.visionSummary.manualChecks.map((item) => (
            <li key={item} className="rounded-lg border border-[#2a2f38] bg-[#15191f] px-3 py-2">{item}</li>
          ))}
        </ul>
      </div>
      {shouldShowBathroomScopeNotice(result) && (
        <div className="mt-3 rounded-2xl border border-amber-400/30 bg-amber-500/10 p-4 text-sm font-semibold text-amber-100">
          {BATHROOM_SCOPE_NOTICE}
        </div>
      )}
      <ManualItemsEditor
        items={manualItems}
        onAdd={onAddManualItem}
        onUpdate={onUpdateManualItem}
        onRemove={onRemoveManualItem}
      />
      <MarginShieldAuditPanel
        audit={marginShieldAudit}
        materialMode={result.materialMode}
        decisions={marginShieldDecisions}
        onDecisionChange={onMarginShieldDecisionChange}
      />
      <div className="mt-3 rounded-2xl border border-[#2a2f38] bg-[#0f1217] p-4">
        <p className="text-sm font-semibold text-white">Materiały, VAT i dane do PDF</p>
        <div className="mt-3 grid gap-2 text-sm text-slate-300 sm:grid-cols-2">
          <EstimateFieldRow label="Materiały" value={getQuickMaterialModeLabel(result.materialMode)} source="manual" />
          <EstimateFieldRow label="Materiały do PDF" value={safeFormatPLN(offerPricing.materialNet)} source="manual" />
          <EstimateFieldRow label="VAT" value={getVatDocumentLabel(result.vatMode)} source="manual" />
          <EstimateFieldRow label="Netto" value={safeFormatPLN(offerPricing.netTotal)} source="manual" />
          <EstimateFieldRow label="Kwota VAT" value={result.vatMode === 'confirm' ? 'do potwierdzenia' : safeFormatPLN(offerPricing.vatAmount)} source="manual" />
          <EstimateFieldRow label="Brutto" value={safeFormatPLN(offerPricing.grossTotal)} source="manual" />
          <EstimateFieldRow label="Wykonawca" value={result.contractorName || 'Oferta wykonawcy'} source="manual" />
          <EstimateFieldRow label="Kontakt" value={result.contractorContact || 'Uzupełnij przed PDF'} source="manual" />
        </div>
        <p className="mt-3 text-xs text-slate-400">
          Dane zakupowe materiałów są widoczne tylko dla Ciebie. PDF klienta pokazuje zaakceptowany zakres, cenę i dane wykonawcy.
        </p>
      </div>
      {materialCatalogItems.length > 0 && (
        <div className="mt-3 rounded-2xl border border-[#2a2f38] bg-[#0f1217] p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-white">Materiały - założenia lokalne</p>
              <p className="mt-1 text-xs text-slate-400">
                {getQuickMaterialModeLabel(result.materialMode)}. Standard materiału nie zmienia wyceny bez decyzji wykonawcy.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {(['ekonomiczny', 'standard', 'premium'] as MaterialCatalogStandard[]).map((standard) => (
                <span
                  key={standard}
                  className={`rounded-full border px-3 py-1 text-xs font-black uppercase tracking-wide ${
                    standard === 'standard'
                      ? 'border-amber-400/50 bg-amber-500/10 text-amber-100'
                      : 'border-[#2a2f38] bg-[#15191f] text-slate-400'
                  }`}
                >
                  {formatMaterialStandard(standard)}
                </span>
              ))}
              <span className="rounded-full border border-[#2a2f38] bg-[#15191f] px-3 py-1 text-xs font-black uppercase tracking-wide text-slate-400">
                Własny
              </span>
            </div>
          </div>
          <div className="mt-3 grid gap-2">
            <div className="rounded-xl border border-amber-400/20 bg-amber-500/10 px-3 py-3 text-sm text-amber-100">
              {result.materialMode === 'contractor' ? (
                <div className="grid gap-2 sm:grid-cols-4">
                  <EstimateFieldRow label="Cena dla klienta" value={safeFormatPLN(materialPreview.materialRetailPrice)} source="manual" />
                  <EstimateFieldRow label="Rabat (%)" value={`${materialPreview.discountPercent}%`} source="manual" />
                  <EstimateFieldRow label="Koszt po rabacie" value={safeFormatPLN(materialPreview.internalMaterialCost)} source="manual" />
                  <EstimateFieldRow label="Zysk na materiale" value={safeFormatPLN(materialPreview.internalMaterialGain)} source="local" />
                </div>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                  <EstimateFieldRow label="Orientacyjny koszt materiałów" value={safeFormatPLN(materialPreview.materialRetailPrice)} source="confirm" />
                  <EstimateFieldRow label="Do PDF klienta" value={safeFormatPLN(offerPricing.materialNet)} source="manual" />
                </div>
              )}
              <p className="mt-2 text-xs text-amber-100/80">
                {result.materialMode === 'contractor'
                  ? 'PDF pokazuje tylko zaakceptowaną cenę materiałów. Dane zakupowe zostają wewnętrzne.'
                  : 'Materiały z katalogu są informacyjne. Własne pozycje materiałowe dodane do oferty są wliczone w cenę.'}
              </p>
            </div>
            {materialCatalogItems.map((item) => (
              <div key={item.id} className="rounded-lg border border-[#2a2f38] bg-[#15191f] px-3 py-2 text-sm">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="font-semibold text-slate-100">{item.name}</p>
                    <p className="text-xs text-slate-400">Źródło: {item.source}</p>
                  </div>
                  <div className="font-black text-amber-100">
                    {formatMaterialStandard(item.standard)} · {item.unitPrice} zł/{item.unit}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="mt-4">
        <p className="text-sm font-semibold text-slate-300">Opcje / zalecenia</p>
        <ul className="mt-2 space-y-2">
          {risks.map((risk) => (
            <li key={risk} className="rounded-lg border border-amber-400/20 bg-amber-500/10 px-3 py-2 text-sm text-amber-100">{risk}</li>
          ))}
        </ul>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <button type="button" onClick={onEdit} className="flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-[#2a2f38] bg-[#0f1217] px-4 font-semibold text-slate-200 transition hover:border-amber-400/40">
          <Pencil className="h-4 w-4" />
          {publicMode ? 'Popraw dane' : 'Popraw ręcznie'}
        </button>
        <button type="button" onClick={onPdf} disabled={isPdfDeliveryPending} aria-busy={isPdfDeliveryPending} className="flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-[#00e676] px-4 font-black text-black shadow-lg shadow-[#00e676]/15 transition hover:bg-emerald-300 disabled:cursor-wait disabled:opacity-70">
          {isPdfDeliveryPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
          {isPdfDeliveryPending ? 'Dostarczanie PDF...' : publicMode ? 'Pobierz roboczy PDF testowy' : 'Generuj PDF'}
        </button>
        {/* Saving needs an account, so a logged-out visitor gets the login CTA
            instead of a save button — never both. */}
        {publicMode ? (
          <button type="button" onClick={onLogin} className="flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-[#2a2f38] bg-[#0f1217] px-4 font-bold text-slate-200 transition hover:border-amber-400/40">
            <FileText className="h-4 w-4" />
            Zaloguj się, aby zapisać projekt
          </button>
        ) : (
          <button type="button" onClick={onSaveProject} disabled={isSavingProject} className="flex min-h-14 items-center justify-center gap-2 rounded-2xl border border-[#2a2f38] bg-[#0f1217] px-4 font-bold text-slate-200 transition hover:border-[#00e676]/40 hover:text-[#00e676] disabled:opacity-50">
            <Save className="h-4 w-4" />
            {isSavingProject ? 'Zapisywanie…' : 'Zapisz projekt'}
          </button>
        )}
        {userLabel && (
          <button type="button" onClick={onProjects} className="flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-[#2a2f38] bg-[#0f1217] px-4 font-bold text-slate-200 transition hover:border-cyan-400/40 hover:text-cyan-100">
            <FolderOpen className="h-4 w-4" />
            Moje projekty
          </button>
        )}
      </div>
      <PwaInstallCard hasGeneratedPdf={hasGeneratedPdf} install={pwaInstall} />
      {pdfStatusMessage && (
        <div className="mt-3 rounded-xl border border-red-400/30 bg-red-500/10 px-3 py-3 text-sm font-semibold text-red-100">
          <p>{pdfStatusMessage}</p>
        </div>
      )}
      {pdfNoticeMessage && (
        <p className="mt-3 rounded-xl border border-[#2a2f38] bg-[#15191f] px-3 py-2 text-sm font-semibold text-slate-300">
          {pdfNoticeMessage}
        </p>
      )}
      {saveStatusMessage && (
        <p className="mt-3 rounded-xl border border-amber-400/25 bg-amber-500/10 px-3 py-2 text-sm font-semibold text-amber-100">
          {saveStatusMessage}
        </p>
      )}
    </section>
  );
});

const MarginShieldAuditPanel = memo(function MarginShieldAuditPanel({
  audit,
  materialMode,
  decisions,
  onDecisionChange,
}: {
  audit: MarginShieldAuditResult;
  materialMode: DomainMaterialMode;
  decisions: Record<string, MarginShieldDecisionStatus>;
  onDecisionChange: (alertId: string, status: MarginShieldDecisionStatus) => void;
}) {
  const [showAll, setShowAll] = useState(false);
  const sortedAlerts = [...audit.alerts].sort((a, b) => getMarginShieldRiskPriority(b.risk) - getMarginShieldRiskPriority(a.risk));
  const visibleAlerts = showAll ? sortedAlerts : sortedAlerts.slice(0, 3);
  const hiddenAlertCount = Math.max(0, sortedAlerts.length - visibleAlerts.length);
  const displayStatus = (alert: MarginShieldAlert) =>
    resolveMarginShieldDisplayStatus(alert.id, decisions[alert.id], materialMode);
  const undecidedAlerts = sortedAlerts.filter((alert) => displayStatus(alert) === 'suggested');
  const addedAlerts = sortedAlerts.filter((alert) => displayStatus(alert) === 'added');
  const undecidedReviewValue = sumMoney(undecidedAlerts.map((alert) => alert.estimatedHiddenLoss));
  const addedValue = sumMoney(addedAlerts.map((alert) => alert.estimatedHiddenLoss));

  return (
    <div className="mt-3 rounded-2xl border border-amber-400/25 bg-amber-500/10 p-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-sm font-black uppercase tracking-[0.18em] text-amber-200">Tarcza Marży: pozycje do sprawdzenia przed wysłaniem</p>
          <p className="mt-1 text-sm text-slate-300">
            Sprawdza pozycje, które często uciekają z wyceny: grunt, folie, taśmy, akryl, odpady, zabezpieczenia i obróbki.
          </p>
        </div>
        {visibleAlerts.length > 0 && (
          <span className="rounded-full border border-amber-400/40 bg-[#0f1217] px-3 py-1 text-xs font-black uppercase tracking-wide text-amber-100">
            {undecidedAlerts.length} do decyzji
          </span>
        )}
      </div>

      {visibleAlerts.length > 0 ? (
        <>
          <p className="mt-3 text-sm font-semibold text-amber-100">
            Orientacyjna wartość pozycji do decyzji: {safeFormatPLN(undecidedReviewValue)}
          </p>
          {addedValue > 0 && (
            <p className="mt-1 text-sm font-semibold text-[#00e676]">
              Dodano do ceny: {safeFormatPLN(addedValue)}
            </p>
          )}
          <div className="mt-3 grid gap-2">
            {visibleAlerts.map((alert) => (
              <MarginShieldAlertRow
                key={alert.id}
                alert={alert}
                status={displayStatus(alert)}
                onAdd={() => onDecisionChange(alert.id, 'added')}
                onSkip={() => onDecisionChange(alert.id, 'skipped')}
                onCheck={() => onDecisionChange(alert.id, 'checked')}
              />
            ))}
          </div>
          <p className="mt-3 text-xs text-slate-400">
            To są pozycje do decyzji wykonawcy. ProWycena nie dolicza ich automatycznie bez zatwierdzenia.
          </p>
          {hiddenAlertCount > 0 && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="mt-3 min-h-10 rounded-xl border border-amber-400/35 bg-[#0f1217] px-4 text-sm font-bold text-amber-100 transition hover:bg-amber-500/10"
            >
              Pokaż więcej ({hiddenAlertCount})
            </button>
          )}
        </>
      ) : (
        <p className="mt-3 rounded-lg border border-[#2a2f38] bg-[#15191f] px-3 py-2 text-sm text-slate-300">
          Nie wykryto oczywistych braków w tej szybkiej wycenie.
        </p>
      )}
    </div>
  );
});

const MarginShieldAlertRow = memo(function MarginShieldAlertRow({
  alert,
  status,
  onAdd,
  onSkip,
  onCheck,
}: {
  alert: MarginShieldAlert;
  status: MarginShieldDecisionStatus | 'informational';
  onAdd: () => void;
  onSkip: () => void;
  onCheck: () => void;
}) {
  if (status === 'informational') {
    return (
      <div className="rounded-lg border border-[#2a2f38] bg-[#15191f] px-3 py-2">
        <p className="text-sm font-semibold text-white">{alert.missingItem}</p>
        <p className="mt-1 text-sm text-slate-300">
          Informacyjnie: potwierdź zapas materiału i docinki. Ta sugestia nie zwiększa ceny w wybranym trybie materiałów.
        </p>
      </div>
    );
  }

  const isAdded = status === 'added';
  const isChecked = status === 'checked';
  const isSkipped = status === 'skipped';

  return (
    <div className={`rounded-lg border border-[#2a2f38] bg-[#15191f] px-3 py-2 ${status === 'skipped' ? 'opacity-70' : ''}`}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-white">{alert.missingItem}</p>
          <p className="mt-1 text-sm text-slate-300">{alert.alertMessage}</p>
          <p className="mt-2 text-sm font-semibold text-amber-100">
            Możliwa wartość pozycji do sprawdzenia: {safeFormatPLN(alert.estimatedHiddenLoss)}
          </p>
          <p className={`mt-2 w-fit rounded-full border px-2.5 py-1 text-xs font-black uppercase tracking-wide ${getMarginShieldStateClass(status)}`}>
            {getMarginShieldStateLabel(status)}
          </p>
        </div>
        <span className={`w-fit rounded-full border px-2.5 py-1 text-xs font-black ${getMarginShieldRiskClass(alert.risk)}`}>
          {getMarginShieldRiskLabel(alert.risk)}
        </span>
      </div>
      <p className="mt-3 text-xs text-slate-400">Sprawdź, czy chcesz uwzględnić tę pozycję.</p>
      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        <button
          type="button"
          onClick={onAdd}
          disabled={isAdded}
          className={`min-h-10 rounded-xl px-3 text-sm font-black transition ${
            isAdded
              ? 'cursor-not-allowed bg-[#00e676]/25 text-[#00e676]'
              : 'bg-[#00e676] text-black hover:bg-emerald-300'
          }`}
        >
          {isAdded ? 'Doliczono' : 'DOLICZ'}
        </button>
        <button
          type="button"
          onClick={onCheck}
          disabled={isChecked}
          className={`min-h-10 rounded-xl border px-3 text-sm font-bold transition ${
            isChecked
              ? 'cursor-not-allowed border-amber-400/25 bg-amber-500/5 text-amber-100/70'
              : 'border-amber-400/35 bg-amber-500/10 text-amber-100 hover:bg-amber-500/15'
          }`}
        >
          {isChecked ? 'Mam w cenie' : 'MAM W CENIE'}
        </button>
        <button
          type="button"
          onClick={onSkip}
          disabled={isSkipped}
          className={`min-h-10 rounded-xl border px-3 text-sm font-bold transition ${
            isSkipped
              ? 'cursor-not-allowed border-slate-600/40 bg-slate-700/10 text-slate-400'
              : 'border-[#2a2f38] bg-[#0f1217] text-slate-200 hover:border-amber-400/40'
          }`}
        >
          {isSkipped ? 'Nie dotyczy' : 'NIE DOTYCZY'}
        </button>
      </div>
    </div>
  );
});

function getMarginShieldStateLabel(state: MarginShieldDecisionStatus): string {
  if (state === 'added') return 'Doliczono';
  if (state === 'skipped') return 'Nie dotyczy';
  if (state === 'checked') return 'Mam w cenie';
  return 'Do decyzji';
}

function getMarginShieldStateClass(state: MarginShieldDecisionStatus): string {
  if (state === 'added') return 'border-[#00e676]/40 bg-[#00e676]/10 text-[#00e676]';
  if (state === 'skipped') return 'border-slate-500/40 bg-slate-500/10 text-slate-300';
  if (state === 'checked') return 'border-amber-400/40 bg-amber-500/10 text-amber-100';
  return 'border-[#2a2f38] bg-[#0f1217] text-slate-300';
}

function getMarginShieldRiskClass(risk: MarginShieldAlert['risk']): string {
  if (risk === 'H') return 'border-red-400/50 bg-red-500/10 text-red-200';
  if (risk === 'M') return 'border-amber-400/50 bg-amber-500/10 text-amber-100';
  return 'border-cyan-400/40 bg-cyan-500/10 text-cyan-100';
}

function getMarginShieldRiskLabel(risk: MarginShieldAlert['risk']): string {
  if (risk === 'H') return 'obowiązkowe';
  if (risk === 'M') return 'do ustalenia';
  return 'opcjonalne';
}

function getMarginShieldRiskPriority(risk: MarginShieldAlert['risk']): number {
  if (risk === 'H') return 3;
  if (risk === 'M') return 2;
  return 1;
}

const EstimateFieldRow = memo(function EstimateFieldRow({
  label,
  value,
  source,
}: {
  label: string;
  value: string;
  source: AreaSource | SourceBadgeTone;
}) {
  return (
    <div className="min-w-0 rounded-lg border border-[#2a2f38] bg-[#15191f] px-3 py-2">
      <div className="flex min-w-0 flex-col items-start gap-1 sm:flex-row sm:justify-between sm:gap-3">
        <span className="min-w-0 break-words text-slate-400">{label}</span>
        <SourceBadge source={source} />
      </div>
      <div className="mt-1 break-words font-semibold text-slate-100">{value}</div>
    </div>
  );
});

const SourceBadge = memo(function SourceBadge({ source }: { source: AreaSource | SourceBadgeTone }) {
  const tone = getSourceBadgeTone(source);
  const className =
    tone === 'manual'
      ? 'border-[#00e676]/40 bg-[#00e676]/10 text-[#00e676]'
      : tone === 'voice'
        ? 'border-cyan-400/40 bg-cyan-500/10 text-cyan-100'
        : tone === 'vision'
          ? 'border-blue-400/40 bg-blue-500/10 text-blue-100'
          : tone === 'local'
            ? 'border-slate-500/40 bg-slate-500/10 text-slate-200'
          : 'border-amber-400/40 bg-amber-500/10 text-amber-100';

  return (
    <span className={`max-w-full whitespace-normal break-words rounded-full border px-2 py-0.5 text-left text-[10px] font-black uppercase leading-tight tracking-wide sm:max-w-[65%] ${className}`}>
      {getSourceBadgeLabel(source)}
    </span>
  );
});
const FieldLabel = memo(function FieldLabel({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm font-semibold text-slate-300">
      {label}
      <div className="mt-1">{children}</div>
    </label>
  );
});
